import { canUseDomainPermission } from "@/utils/domainPermissions";
import axios from "axios";
import Vue from "vue";
import confetti from "canvas-confetti";
import store from "@/stores/store";
import storeutils from "@/stores/utils";
import { Decimal } from "decimal.js";
import messages from "@/i18n/messages";
import {
    CUSTOMER_INVOICE_PERMISSIONS,
    CUSTOMER_ORDER_PERMISSIONS,
    PROVIDER_CATALOG_PERMISSIONS,
    PROVIDER_INVOICE_PERMISSIONS,
    PROVIDER_ORDER_PERMISSIONS,
    SELL_CATALOG_PERMISSIONS,
    resolvePermissionCheck,
    userCanManageCatalogAssignments,
    userHasAnyPermission,
} from "@/utils/groupPermissions";
import {
    isCustomerOrderAwaitingBuyerConfirmation,
} from "@/utils/purchaseApproval";
import { orderInvoicePendingChip } from "@/utils/customer-order-invoice-flow";
import {
    DEFAULT_CURRENCY,
    formatAmount,
    formatNumber,
    formatQuantity,
} from "@/utils/numberFormat";
import {
    FORMAT_SELECT_CREATE,
    FORMAT_SELECT_CUSTOM,
    FORMAT_SELECT_UNIT,
} from "@/constants/format-select";
import {
    buildFormatContainerOptions as cascadeBuildContainerOptions,
    formatLabelMatchesSearch as cascadeFormatLabelMatchesSearch,
    formatsForContainer as cascadeFormatsForContainer,
    getFormatContainerKey as cascadeContainerKey,
    getFormatContainerLabel as cascadeContainerLabel,
    quantitiesForContainerAndUnit as cascadeQuantitiesForContainerAndUnit,
    resolveFormat as cascadeResolveFormat,
    unitsForContainer as cascadeUnitsForContainer,
} from "@/utils/formatCascade";

const ICONS = {
    // Generic
    account: "mdi-account-cog",
    activity: "mdi-briefcase",
    assign: "mdi-account-multiple-plus",
    add: "mdi-add",
    more: "mdi-arrow-down-drop-circle-outline",
    address: "mdi-map-marker",
    addtoorder: "mdi-cart-outline",
    addtoreceipt: "mdi-dolly",
    addtoshipment: "mdi-truck-fast-outline",
    agent: "mdi-account",
    alert: "mdi-alert-circle-check-outline",
    allocation: "mdi-star-outline",
    api: "mdi-api",
    arrowRight: "mdi-arrow-right",
    back: "mdi-arrow-left",
    down: "mdi-arrow-down",
    up: "mdi-arrow-up",
    bottle: "mdi-bottle-wine-outline",
    cancel: "mdi-cancel",
    catalog: "mdi-book-open-outline",
    check: "mdi-check",
    checkall: "mdi-check-all",
    code: "mdi-barcode",
    csv: "mdi-file-delimited",
    country: "mdi-earth",
    link: "mdi-link",
    default: "mdi-cards-heart-outline",
    delete: "mdi-delete-outline",
    download: "mdi-file-download-outline",
    duplicate: "mdi-content-copy",
    edit: "mdi-pencil",
    email: "mdi-email",
    euro: "mdi-currency-eur",
    eye: "mdi-eye-outline",
    feature: "mdi-cards-playing-heart-multiple-outline",
    home: "mdi-home",
    inventory: "mdi-clipboard-list-outline",
    invitations: "mdi-message-badge-outline",
    instructions: "mdi-message-badge-outline",
    invoice: "mdi-receipt-outline",
    items: "mdi-cube-unfolded",
    login: "mdi-login",
    terms: "mdi-scale-balance",
    magnify: "mdi-magnify",
    missing: "mdi-alert-circle",
    mobilephone: "mdi-cellphone",
    movement: "mdi-plus-minus-variant",
    name: "mdi-card-account-details-outline",
    note: "mdi-note",
    open: "mdi-open-in-new",
    order: "mdi-cart-outline",
    orders: "mdi-cart-outline",
    pdf: "mdi-file-pdf-box",
    phone: "mdi-phone",
    payment: "mdi-cash",
    sale: "mdi-cash-register",
    registersale: "mdi-cash-register",
    placed: "mdi-bookshelf",
    plus: "mdi-plus",
    pluscircle: "mdi-plus-circle-outline",
    printer: "mdi-printer",
    product: "mdi-cube-outline",
    production: "mdi-pot-steam-outline",
    products: "mdi-cube-outline",
    prospect: "mdi-account-search",
    refund: "mdi-cash-refund",
    release: "mdi-star",
    reload: "mdi-reload",
    scale: "mdi-scale",
    save: "mdi-content-save-outline",
    search: "mdi-magnify",
    shipmentprepared: "mdi-package-variant-closed",
    shipped: "mdi-truck-fast-outline",
    site: "mdi-map-marker",
    status: "mdi-state-machine",
    take: "mdi-star-outline",
    stock: "mdi-counter",
    warehouse: "mdi-warehouse",
    stockid: "mdi-music-accidental-sharp",
    stocklocation: "mdi-map-outline",
    technicalsheet: "mdi-file-document-edit-outline",
    upc: "mdi-ticket-confirmation-outline",
    upload: "mdi-upload",
    packaging: "mdi-inbox-multiple-outline",
    producttype: "mdi-format-list-bulleted-type",

    shipment: "mdi-truck-delivery-outline",
    shipments: "mdi-truck-delivery-outline",
    receipt: "mdi-dolly",
    receipts: "mdi-dolly",
    billing: "mdi-currency-eur",
    price: "mdi-currency-eur",
    settings: "mdi-cog-outline",
    date: "mdi-calendar",
    delay: "mdi-calendar-plus",
    user: "mdi-account",

    // Entities
    operator: "mdi-account-group-outline",
    customer: "mdi-account-box-outline",
    /** Alias for filters / labels meaning "economic owner" (same glyph as customer). */
    entity: "mdi-account-box-outline",
    owner: "mdi-account-box-outline",
    customers: "mdi-account-box-multiple-outline",
    deliverysite: "mdi-domain",
    establishment: "mdi-store",
    establishments: "mdi-store-outline",
    store: "mdi-store",
    provider: "mdi-guy-fawkes-mask",
    providers: "mdi-drama-masks",
    manufacturer: "mdi-hammer",
    year: "mdi-calendar",
    siren: "domain",
    vat: "mdi-currency-eur",
    billingaccount: "mdi-briefcase-account",

    alarm: "mdi-alarm",
    comment: "mdi-comment",
    euros: "mdi-currency-eur",
    archive: "mdi-archive",
    percent: "mdi-percent",
    text: "mdi-text",
    import: "mdi-import",
    favorite: "mdi-heart",
    category: "mdi-folder-outline",
    tags: "mdi-tag-multiple-outline",
    send: "mdi-send-variant-outline",
    unarchive: "mdi-package-up",
    info: "mdi-information",
    persona: "mdi-alpha-p-circle",
    format: "mdi-glass-wine",
    saleformat: "mdi-glass-wine",
    notes: "mdi-comment-text-multiple-outline",
    content_copy: "mdi-content-copy",
    weight: "mdi-weight-kilogram",
};

export default {
    parseOptionalNumberInput(value) {
        if (value === "" || value === null || value === undefined) {
            return null;
        }
        // Tolerate comma decimal separator.
        const parsed = parseFloat(String(value).replace(",", "."));
        return Number.isFinite(parsed) ? parsed : null;
    },
    isSetNumber(value) {
        return value !== null && value !== undefined && Number.isFinite(Number(value));
    },
    getTruncatedCatalogList(catalogs, max) {
        if (!catalogs?.length) {
            return Vue.prototype.$translate("customer.no_catalogs_assigned");
        }
        let filtered_catalogs = [];
        let extra_catalogs = [];
        let text = "";

        if (catalogs.length > max) {
            filtered_catalogs = catalogs.slice(0, max);
            extra_catalogs = catalogs.length - max;
        } else {
            filtered_catalogs = catalogs;
        }

        const catalogName = (catalog) => {
            if (catalog.owner == this.current_entity_id) {
                return catalog.name;
            }
            return `${catalog.owner_data.name} - ${catalog.name}`;
        };

        text = filtered_catalogs.map(catalogName).join(", ");

        if (extra_catalogs > 0) {
            text += ` & ${extra_catalogs} ${Vue.prototype.$translate(
                "andmore"
            )}`;
        }
        return text;
    },
    shareCapitalRequired(legal_structure) {
        return !["ei", "eirl", "auto"].includes(legal_structure);
    },
    rcsRequired(legal_structure) {
        return !["ei", "eirl", "earl", "scea", "scev", "auto"].includes(
            legal_structure
        );
    },
    triggerFireworks() {
        confetti({
            particleCount: 100,
            spread: 100,
            origin: { y: 0.8 },
            scalar: 1.5,
        });
    },
    getStockName(stock) {
        if (!stock) {
            return "";
        }
        return stock.owned
            ? stock.name
            : `${stock.name} (${
                  stock.owner_data?.name ||
                  stock.owner_name ||
                  Vue.prototype.$translate("external")
              })`;
    },
    /**
     * Whether the current entity owns the warehouse (same rule as shipment delivery_date
     * validation: initiator == stock.owner). Prefer API `owned` when present.
     */
    isSelfManagedStock(stock, currentEntityId) {
        if (!stock || typeof stock !== "object") {
            return false;
        }
        if (stock.owned === true) {
            return true;
        }
        if (stock.owned === false) {
            return false;
        }
        const stockOwner = stock.owner ?? stock.owner_data?.id;
        if (stockOwner == null || currentEntityId == null) {
            return false;
        }
        return Number(stockOwner) === Number(currentEntityId);
    },
    getLegalStructureItems() {
        return [
            {
                value: "auto",
                text: Vue.prototype.$translate("legalstructure.auto"),
            },
            {
                value: "ei",
                text: Vue.prototype.$translate("legalstructure.ei"),
            },
            {
                value: "earl",
                text: Vue.prototype.$translate("legalstructure.earl"),
            },
            {
                value: "eirl",
                text: Vue.prototype.$translate("legalstructure.eirl"),
            },
            {
                value: "eurl",
                text: Vue.prototype.$translate("legalstructure.eurl"),
            },
            {
                value: "sa",
                text: Vue.prototype.$translate("legalstructure.sa"),
            },
            {
                value: "sarl",
                text: Vue.prototype.$translate("legalstructure.sarl"),
            },
            {
                value: "sas",
                text: Vue.prototype.$translate("legalstructure.sas"),
            },
            {
                value: "sasu",
                text: Vue.prototype.$translate("legalstructure.sasu"),
            },
            {
                value: "scea",
                text: Vue.prototype.$translate("legalstructure.scea"),
            },
            {
                value: "scev",
                text: Vue.prototype.$translate("legalstructure.scev"),
            },
            {
                value: "snc",
                text: Vue.prototype.$translate("legalstructure.snc"),
            },
        ].sort((a, b) => a.text.localeCompare(b.text));
    },
    getCustomerOrderStatusText(order) {
        if (isCustomerOrderAwaitingBuyerConfirmation(order)) {
            return Vue.prototype.$translate("customerorder.status.validated");
        }
        if (order.status) {
            return Vue.prototype.$translate(
                `customerorder.status.${order.status}`
            );
        }
        return Vue.prototype.$translate("customerorder.status.waiting");
    },
    getCustomerOrderStatusTooltip(order) {
        if (order?.status === "to_validate") {
            return Vue.prototype.$translate("customerorder.statusTooltip.to_validate");
        }
        if (!order?.ready || order.status === "draft") {
            return null;
        }
        if (
            order.status === "to_be_processed" ||
            order.status === "partially_processed"
        ) {
            return Vue.prototype.$translate(
                `customerorder.statusTooltip.${order.status}`
            );
        }
        return null;
    },
    getAgentCommissionStatusText(item) {
        switch (item.agent_commission_status) {
            case "waiting":
                return Vue.prototype.$translate(
                    "agentcommission.status.waiting"
                );
            case "ready":
                return Vue.prototype.$translate("agentcommission.status.ready");
            case "billed":
                return Vue.prototype.$translate(
                    "agentcommission.status.billed"
                );
            case "paid":
                return Vue.prototype.$translate("agentcommission.status.paid");
            case "ignored":
                return Vue.prototype.$translate(
                    "agentcommission.status.ignored"
                );
            default:
                return item.agent_commission_status;
        }
    },
    getAgentCommissionStatusColor(item) {
        if (item.agent_commission_status === "ready") {
            return "warning";
        }
        return null;
    },

    getCustomerOrderStatusColor(order) {
        if (order?.status === "to_validate") {
            return "warning";
        }
        if (isCustomerOrderAwaitingBuyerConfirmation(order)) {
            return "success";
        }
        if (!order.ready || order.status === "draft") {
            return "gray";
        }
        switch (order.status) {
            case "to_be_processed":
                return "warning";
            case "partially_processed":
                return "deep-orange";
            case "waiting":
                return "primary";
            case "processing":
                return "primary";
            case "completed":
                return "success";
            case "canceled":
                return "gray";
        }
    },
    getProviderOrderStatusText(order) {
        return Vue.prototype.$translate(`providerorder.status.${order.status}`);
    },

    getProviderOrderStatusColor(order) {
        if (!order.ready || order.status === "draft") {
            return "gray";
        }

        switch (order.status) {
            case "to_be_processed":
                return "warning";
            case "waiting":
                return "primary";
            case "processing":
                return "primary";
            case "completed":
                return "success";
            case "canceled":
                return "gray";
        }
    },
    getStatusText(status) {
        const statusTexts = {
            canceled: Vue.prototype.$translate("status.canceled"),
            closed: Vue.prototype.$translate("status.closed"),
            complete: Vue.prototype.$translate("status.complete"),
            completed: Vue.prototype.$translate("status.completed"),
            delivered: Vue.prototype.$translate("status.delivered"),
            exhausted: Vue.prototype.$translate("status.exhausted"),
            handling: Vue.prototype.$translate("status.handling"),
            missing: Vue.prototype.$translate("status.missing"),
            new: Vue.prototype.$translate("status.new"),
            opened: Vue.prototype.$translate("status.opened"),
            partial: Vue.prototype.$translate("status.partial"),
            pending: Vue.prototype.$translate("status.pending"),
            picked_up: Vue.prototype.$translate("status.pickedUp"),
            prepared: Vue.prototype.$translate("status.prepared"),
            preparing: Vue.prototype.$translate("status.preparing"),
            processed: Vue.prototype.$translate("status.processed"),
            processing: Vue.prototype.$translate("status.processing"),
            shipping: Vue.prototype.$translate("status.shipping"),
            waiting: Vue.prototype.$translate("status.waiting"),
        };
        return statusTexts[status] || status;
    },

    getStatusColor(status) {
        return (
            {
                complete: "success",
                delivered: "success",
                picked_up: "success",
                canceled: "gray",
                waiting: "warning",
            }[status] || "grey"
        );
    },
    getPersonaLabel(persona) {
        if (!persona) {
            return "";
        }
        return Vue.prototype.$translate(`persona.${persona}`);
    },
    getPersonasChoices() {
        return ["wholesaler", "retailer", "hospitality_group", "manufacturer", "agent", "other"].map(
            (persona) => {
                return {
                    value: persona,
                    text: this.getPersonaLabel(persona),
                };
            }
        );
    },
    toPercent(value) {
        return new Decimal(value).mul(100).toNumber();
    },
    customerTypeText(type) {
        const CUSTOMER_TYPES_TEXTS = {
            individual: Vue.prototype.$translate("customer.type.individual"),
            company: Vue.prototype.$translate("customer.type.company"),
        };

        return CUSTOMER_TYPES_TEXTS[type];
    },

    icon(code) {
        if (!ICONS[code]) {
            return code;
        }
        return ICONS[code];
    },
    formatNumberToString(value, unit, float_part = false) {
        if (value === null || value === undefined) {
            return "-";
        }
        const amount = new Decimal(value);
        // Integers without forced decimals: no fraction digits; otherwise 2.
        const decimals = amount.isInteger() && !float_part ? 0 : 2;
        const formatted = formatNumber(amount.toNumber(), {
            minDecimals: decimals,
            maxDecimals: decimals,
        });
        return formatted + (unit ? ` ${unit}` : "");
    },
    price(value, currency = DEFAULT_CURRENCY) {
        return formatAmount(value, { currency });
    },
    percent(value) {
        if (value === null || value === undefined) {
            return "- %";
        }
        // Convert from 0-1 decimal to 0-100 percentage
        return this.formatNumberToString(value * 100, "%", false);
    },
    number(value) {
        if (value === null) {
            return "-";
        }
        return this.formatNumberToString(value);
    },
    decimal(value) {
        if (value === null || value === undefined || value === "") {
            return null;
        }
        try {
            return new Decimal(value);
        } catch (e) {
            return null;
        }
    },
    getInvoiceIdText(item) {
        if (item.status == "draft") {
            return Vue.prototype.$translate("customerinvoice.status.draft");
        }
        return `#${item.invoice_id}`;
    },
    getReceiptStatusText(item) {
        if (item.status == "waiting" && !item.ready) {
            return Vue.prototype.$translate("receipt.status.draft");
        }
        if (
            item.status == "waiting" &&
            item.operator == store.getters["session/current_entity_id"]
        ) {
            return Vue.prototype.$translate("receipt.status.to_be_processed");
        }

        return Vue.prototype.$translate(`receipt.status.${item.status}`);
    },
    getReceiptStatusColor(item) {
        if (!item.ready) {
            return "gray";
        }
        if (item.status == "partial") {
            return "warning";
        }
        if (item.status == "waiting") {
            return "warning";
        }
        if (item.status == "handling") {
            return "primary";
        }

        if (
            ["picked_up", "delivered", "complete", "shipping"].includes(
                item.status
            )
        ) {
            return "success";
        }
        return "default";
    },
    getShipmentStatusText(item) {
        if (item.status == "waiting" && !item.ready) {
            return Vue.prototype.$translate(`shipment.status.draft`);
        }
        if (item.status == "waiting") {
            if (item.operator == store.getters["session/current_entity_id"]) {
                return Vue.prototype.$translate(
                    `shipment.status.to_be_processed`
                );
            }
            return Vue.prototype.$translate(`shipment.status.waiting`);
        }
        if (item.status == "complete") {
            if (item.deliverysite) {
                return Vue.prototype.$translate(`shipment.status.delivered`);
            }
            if (item.recipient) {
                return Vue.prototype.$translate(`shipment.status.picked_up`);
            }
            return Vue.prototype.$translate(`shipment.status.complete`);
        }
        return Vue.prototype.$translate(`shipment.status.${item.status}`);
    },
    getShipmentStatusColor(item) {
        if (!item.ready) {
            // Use a real Vuetify Material tone (not the invalid "gray" token).
            return "blue-grey";
        }
        if (["waiting"].includes(item.status)) {
            return "warning";
        }
        if (["preparing", "prepared"].includes(item.status)) {
            return "primary";
        }

        if (item.status == "shipping") {
            return "primary";
        }
        if (["picked_up", "delivered", "complete"].includes(item.status)) {
            return "success";
        }
        return "default";
    },
    getProviderInvoiceStatusColor(item) {
        if (item.status == "draft") {
            return "gray";
        }
        if (item.status == "tovalidate") {
            return "warning";
        }
        if (item.paid) {
            return "success";
        }
        if (this.isInvoiceOverDue(item)) {
            return "warning";
        }
        if (this.isInvoiceDue(item)) {
            return "error";
        }
        return null;
    },
    getProviderInvoiceStatusOutlined(item) {
        if (item.status == "tovalidate") {
            return false;
        }
        return (
            item.paid ||
            (item.status == "validated" && !this.isInvoiceOverDue(item))
        );
    },

    getProviderInvoiceStatusText(providerinvoice) {
        if (providerinvoice.status == "tovalidate") {
            return Vue.prototype.$translate(
                "providerinvoice.status.tovalidate"
            );
        }
        if (providerinvoice.status == "draft") {
            return Vue.prototype.$translate("providerinvoice.status.draft");
        }

        if (!providerinvoice.paid) {
            if (this.isInvoiceDue(providerinvoice)) {
                return Vue.prototype.$translate("providerinvoice.status.due");
            }
            return Vue.prototype.$translate("providerinvoice.status.waiting");
        }
        return Vue.prototype.$translate("providerinvoice.status.paid");
    },
    isInvoiceOverDue(invoice) {
        return Vue.prototype
            .$moment(invoice.due_date)
            .isBefore(Vue.prototype.$moment(), "day");
    },
    isInvoiceDue(invoice) {
        return Vue.prototype
            .$moment(invoice.due_date)
            .isSameOrBefore(Vue.prototype.$moment(), "day");
    },
    isOrderInvoiceOverDue(order) {
        if (!order?.invoices_next_due_date) {
            return false;
        }
        return Vue.prototype
            .$moment(order.invoices_next_due_date)
            .isBefore(Vue.prototype.$moment(), "day");
    },
    isOrderPaymentOverdue(order) {
        if (!order || order.paid || order.status === "canceled") {
            return false;
        }
        if (this.isRegisterSaleOrder(order)) {
            return false;
        }
        if (!order.invoices_sent || !order.invoices_next_due_date) {
            return false;
        }
        return this.isOrderInvoiceOverDue(order);
    },
    isOrderInvoiceDue(order) {
        return Vue.prototype
            .$moment(order.invoices_next_due_date)
            .isSameOrBefore(Vue.prototype.$moment(), "day");
    },
    getCustomerInvoiceStatusColor(item) {
        if (item.status == "draft") {
            // Warning is reserved for "validated but not sent yet" below, which is
            // the state that actually waits on the user.
            return "gray";
        }
        if (item.paid) {
            return "success";
        }
        if (this.isInvoiceOverDue(item)) {
            return "error";
        }
        if (this.isInvoiceDue(item)) {
            return "primary";
        }
        if (!item.sent) {
            return "warning";
        }
        return null;
    },
    getCustomerInvoiceStatusText(item) {
        if (item.status == "draft") {
            return Vue.prototype.$translate("customerinvoice.status.draft");
        }
        if (item.paid) {
            return item.credit
                ? Vue.prototype.$translate("customerinvoice.status.cashed")
                : Vue.prototype.$translate("customerinvoice.status.paid");
        }
        if (this.isInvoiceOverDue(item)) {
            return Vue.prototype.$translate("customerinvoice.status.overdue");
        }
        if (this.isInvoiceDue(item)) {
            return Vue.prototype.$translate("customerinvoice.status.due");
        }
        if (!item.sent) {
            return Vue.prototype.$translate("customerinvoice.status.tosend");
        }
        return Vue.prototype.$translate(
            "customerinvoice.status.waitingpayment"
        );
    },
    getCustomerInvoiceStatusOutlined(item) {
        if (item.paid) {
            return true;
        }
        if (item.status == "validated") {
            if (!item.sent) {
                return false;
            }
            if (!this.isInvoiceDue(item)) {
                return true;
            }
        }
        return false;
    },

    isRegisterSaleOrder(item) {
        if (!item) {
            return false;
        }
        if (item.is_register_sale === true) {
            return true;
        }
        const orderData = item.order_data;
        return !!(orderData && orderData.is_register_sale === true);
    },

    getOrderInvoicesStatusText(item) {
        if (this.isRegisterSaleOrder(item)) {
            return null;
        }
        if (!item.ready) {
            return null;
        }
        if (item.status == "canceled") {
            return Vue.prototype.$translate(
                "orderinvoices.status.order_canceled"
            );
        }
        if (!item.invoices_ready) {
            return Vue.prototype.$translate(
                "orderinvoices.status." + orderInvoicePendingChip(item).key
            );
        }
        if (!item.invoices_sent) {
            return Vue.prototype.$translate("orderinvoices.status.to_send");
        }
        if (item.paid) {
            return Vue.prototype.$translate("orderinvoices.status.paid");
        }
        if (this.isOrderInvoiceOverDue(item)) {
            return Vue.prototype.$translate(
                "orderinvoices.status.payment_overdue"
            );
        }
        if (this.isOrderInvoiceDue(item)) {
            return Vue.prototype.$translate("orderinvoices.status.payment_due");
        }
        if (!item.paid) {
            return Vue.prototype.$translate(
                "orderinvoices.status.waitingpayment"
            );
        }
        return item.status;
    },
    getOrderInvoiceStatusOutlined(order) {
        if (!order.readonly) {
            return false;
        }
        if (order.status == "canceled") {
            return true;
        }
        if (!order.invoices_ready) {
            return false;
        }
        if (this.isOrderInvoiceOverDue(order)) {
            return false;
        }
        if (this.isOrderInvoiceDue(order)) {
            return false;
        }

        if (!order.invoices_sent) {
            return false;
        }
        if (!order.paid) {
            return true;
        }
        return true;
    },

    getOrderInvoicesStatusColor(order) {
        if (!order.ready) {
            return "gray";
        }
        if (order.status == "canceled") {
            return "gray";
        }
        if (!order.invoices_ready) {
            return orderInvoicePendingChip(order).color;
        }
        if (this.isOrderInvoiceOverDue(order)) {
            return "error";
        }
        if (this.isOrderInvoiceDue(order)) {
            return "error";
        }
        if (!order.invoices_sent) {
            return "warning";
        }
        if (!order.paid) {
            return "primary";
        }
        if (order.paid) {
            return "success";
        }
        return order.status;
    },

    flattenApiErrorMessages(value) {
        if (!value) {
            return [];
        }
        if (Array.isArray(value)) {
            return value.flatMap((item) =>
                typeof item === "object" && item !== null
                    ? this.flattenApiErrorMessages(item)
                    : [String(item)],
            );
        }
        if (typeof value === "object") {
            return Object.values(value).flatMap((item) =>
                this.flattenApiErrorMessages(item),
            );
        }
        return [String(value)];
    },

    snakeToCamelCase(value) {
        return String(value).replace(/_([a-z])/g, (_, char) =>
            char.toUpperCase(),
        );
    },

    apiErrorFieldLabelKeys(fieldKey) {
        const aliases = {
            billingaccount: ["purchaseBillingAccount", "billingAccount"],
            credit: ["creditDebit"],
            due_date: ["dueDate", "variables.dueDate"],
            invoice_date: ["invoiceDate", "creditDate", "variables.invoiceDate"],
            invoice_id: ["invoiceId", "creditNoteId", "variables.invoiceId"],
            order: ["orderId", "purchaseOrderOptional"],
            provider: ["provider"],
            total_amount_excl_vat: ["totalExclVat", "variables.totalExclVat"],
            total_amount_incl_vat: ["totalInclVat", "variables.totalInclVat"],
            total_vat_amount: [
                "totalVat",
                "totalVatAmount",
                "variables.totalVatAmount",
            ],
        };
        const camel = this.snakeToCamelCase(fieldKey);
        return [
            ...(aliases[fieldKey] || []),
            fieldKey,
            camel,
            `variables.${camel}`,
        ];
    },

    resolveApiErrorFieldLabel(view, fieldKey) {
        if (!fieldKey) {
            return null;
        }
        const translateFn =
            view && typeof view.$translate === "function"
                ? (key) => view.$translate(key)
                : Vue.prototype.$translate
                  ? (key) => Vue.prototype.$translate(key)
                  : null;
        const keyExists =
            view && typeof view.$te === "function"
                ? (key) => view.$te(key)
                : (key) => Boolean(Vue.prototype.i18n?.te?.(key));

        if (translateFn) {
            for (const key of this.apiErrorFieldLabelKeys(fieldKey)) {
                if (!keyExists(key)) {
                    continue;
                }
                return String(translateFn(key))
                    .replace(/\s*\(\*\)\s*$/u, "")
                    .trim();
            }
        }

        if (fieldKey.includes("_")) {
            return fieldKey
                .split("_")
                .filter(Boolean)
                .map((part, index) =>
                    index === 0
                        ? part.charAt(0).toUpperCase() + part.slice(1)
                        : part,
                )
                .join(" ");
        }
        return fieldKey;
    },

    collectDetailsErrorMessages(details, view) {
        if (!details || typeof details !== "object") {
            return [];
        }
        const skip = new Set(["unallowed_permissions"]);
        const messages = [];
        for (const [key, value] of Object.entries(details)) {
            if (skip.has(key) || key === "detail") {
                continue;
            }
            const label = this.resolveApiErrorFieldLabel(view, key);
            this.flattenApiErrorMessages(value).forEach((message) => {
                messages.push(label ? `${label} : ${message}` : message);
            });
        }
        return messages;
    },

    resultToSwalContent(view, result) {
        if (result?.response?.data !== undefined) {
            result = storeutils.handleError(result);
        }

        let non_field_errors = null;
        let details = null;

        if (result?.details) {
            details = JSON.parse(JSON.stringify(result.details));
        }
        if (Array.isArray(details)) {
            let content = '<div class="sweeterrors"><ul>';
            details.forEach((error) => {
                content += `<li>${error}</li>`;
            });
            content += "</ul></div>";
            return content;
        }
        if (typeof details === "string") {
            return `<div class="sweeterrors"><ul><li>${details}</li></ul></div>`;
        }
        if (details && details.non_field_errors) {
            non_field_errors = details.non_field_errors;
            delete details.non_field_errors;
        }
        const unallowed_permissions = details?.unallowed_permissions || [];
        if (details?.unallowed_permissions) {
            delete details.unallowed_permissions;
        }
        if (!details) {
            const fallbackMessage =
                result?.message ||
                Vue.prototype.$translate("errors.unknown_error_occurred");
            return (
                '<div class="sweeterrors">' +
                `<ul><li>${fallbackMessage}</li></ul>` +
                "</div>"
            );
        }
        let content = '<div class="sweeterrors">';
        if (details.detail) {
            const detailMessages = Array.isArray(details.detail)
                ? details.detail
                : [details.detail];
            content += "<ul>";
            detailMessages.forEach((message) => {
                content += `<li>${message}</li>`;
            });
            content += "</ul>";
        } else if ([400, 412].indexOf(result.code) != -1) {
            content += "<ul>";
            const fieldMessages = this.collectDetailsErrorMessages(
                details,
                view,
            );
            if (non_field_errors?.length) {
                non_field_errors.forEach((error) => {
                    content += `<li>${error}</li>`;
                });
            }
            if (fieldMessages.length) {
                fieldMessages.forEach((error) => {
                    content += `<li>${error}</li>`;
                });
            } else if (!non_field_errors?.length) {
                if (details && Object.keys(details).length) {
                    if (details.ordering) {
                        content += `<li>${Vue.prototype.$translate("errors.invalidOrdering")}</li>`;
                    } else {
                        content += `<li>${Vue.prototype.$translate(
                            "Fields errors, check form errors and try again"
                        )}</li>`;
                    }
                } else {
                    content += `<li>${Vue.prototype.$translate(
                        "errors.server_reported_bad_form_values"
                    )}</li>`;
                }
            }
            content += "</ul>";
            if (unallowed_permissions.length) {
                content += `<p><strong>${Vue.prototype.$translate(
                    "errors.unallowedPermissions"
                )}</strong></p><ul>`;
                unallowed_permissions.forEach((permission) => {
                    const label = permission.name || permission.codename;
                    content += `<li>${label} (${permission.codename})</li>`;
                });
                content += "</ul>";
            }
        } else if ([403].indexOf(result.code) != -1) {
            if (non_field_errors) {
                content += `${Vue.prototype.$translate(
                    "errors.this_action_cannot_be_performed"
                )}<br/>`;
                content += "<ul>";
                (non_field_errors || []).forEach((error) => {
                    content += `<li>${error}</li>`;
                });
                content += "</ul>";
            } else {
                content +=
                    result.details ||
                    `${Vue.prototype.$translate("errors.permission_denied")}`;
            }
        } else {
            content += "<ul>";
            content += `<li>${Vue.prototype.$translate(
                "errors.an_unexpected_error_happened"
            )}</li>`;
            content += "</ul>";
        }

        content += "</div>";
        return content;
    },

    resultToSwalContentForField(view, result, field) {
        let non_field_errors = null;
        let details = null;
        if (result.details) {
            details = JSON.parse(JSON.stringify(result.details));
        }
        if (details && details.non_field_errors) {
            non_field_errors = details.non_field_errors;
            delete details.non_field_errors;
        }

        let content = '<div class="sweeterrors"><ul>';
        if ([400, 412].indexOf(result.code) != -1) {
            if (details && Object.keys(details).length) {
                content += `<li>${details[field]}</li>`;
            } else if (non_field_errors) {
                (non_field_errors || []).forEach((error) => {
                    content += `<li>${error}</li>`;
                });
            } else {
                content += `<li>${Vue.prototype.$translate(
                    "errors.server_reported_bad_form_values"
                )}</li>`;
            }
        } else if (details.detail) {
            content += `<li>${details.detail}</li>`;
        } else {
            content += `<li>${Vue.prototype.$translate(
                "errors.an_unexpected_error_happened"
            )}</li>`;
        }

        content += "</ul></div>";
        return content;
    },
    humanDate(date) {
        // Full textual date
        return Vue.prototype.$moment(date).format("dddd Do MMMM YYYY, hh:mm");
    },
    isAddressComplete(address) {
        return !!(
            address?.emails?.length &&
            address?.city &&
            address?.line1 &&
            address?.postal_code
        );
    },
    isAddressEmpty(address) {
        return !(
            address?.line1 ||
            address?.line2 ||
            address?.postal_code ||
            address?.city
        );
    },
    pickDeliverySiteAddress(sites) {
        if (!sites?.length) {
            return null;
        }
        const active = sites.filter((site) => !site.archived);
        const site = active.find((item) => item.default) || active[0];
        if (!site?.address || this.isAddressEmpty(site.address)) {
            return null;
        }
        return site.address;
    },
    isAddressCompleteIgnoringEmail(address) {
        return !!(
            address?.city &&
            address?.line1 &&
            address?.postal_code
        );
    },
    isEntityInformationComplete(entity) {
        return !!(
            this.isAddressComplete(entity.billing_address) && entity.siren
        );
    },
    isCustomerInformationComplete(customer) {
        return !!(
            this.isAddressComplete(customer.billing_address) &&
            (customer.siren || customer.type == "individual")
        );
    },
    isCustomerInformationCompleteIgnoringEmail(customer) {
        return !!(
            this.isAddressCompleteIgnoringEmail(customer.billing_address) &&
            (customer.siren || customer.type == "individual")
        );
    },
    customerHasEmail(customer) {
        return !!customer?.billing_address?.emails?.length;
    },
    isBillingAccountComplete(billingaccount) {
        if (!billingaccount) {
            return false;
        }
        return (
            billingaccount.siren &&
            this.isAddressComplete(billingaccount.address)
        );
    },
    async lookup(object, queryparams) {
        try {
            const response = await axios.get(`/api/${object}s/`, {
                params: queryparams,
            });
            if (response.data.results.length) {
                return response.data.results[0].id;
            }
        } catch (xhr_error) {
            return null;
        }
    },
    emailIsValid(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    traverse(dict, path) {
        return path.split(".").reduce((acc, part) => acc && acc[part], dict);
    },

    cleanupMetadata(metadata, producttype) {
        if (!metadata || !producttype) {
            return metadata;
        }
        const new_metadata = {};
        for (const key in metadata) {
            const field = producttype.fields.find((item) => item.code === key);
            if (!field) {
                continue;
            }
            const value = metadata[key];
            if (field.type === "boolean") {
                new_metadata[key] = value === "true" || value === true;
            } else if (field.type === "float") {
                const float = parseFloat(value);
                new_metadata[key] = isNaN(float) ? null : float;
            } else if (field.type === "integer" || field.type === "number") {
                const int = parseInt(value);
                new_metadata[key] = isNaN(int) ? null : int;
            } else if (field.type === "string") {
                new_metadata[key] = value;
            } else if (field.type === "choices") {
                if (field.choices.includes(value)) {
                    new_metadata[key] = value;
                } else {
                    new_metadata[key] = null;
                }
            } else {
                console.log(`field ${key} has unknown type ${field.type}`);
                new_metadata[key] = value;
            }
        }
        return new_metadata;
    },

    userHasPermission(perm) {
        if (
            store.getters["session/readonly"] &&
            (perm.indexOf(".change_") != -1 ||
                perm.indexOf(".add_") != -1 ||
                perm.indexOf(".delete_") != -1)
        ) {
            return false;
        }
        const userPermissions = store.getters["session/current_user_permissions"];
        return resolvePermissionCheck(userPermissions, perm);
    },
    // Domain APIs explicitly grant owners access; mirror that rule without
    // allowing the support account to bypass the impersonated user's rights.
    userHasDomainPermission(perm) {
        return canUseDomainPermission({
            owner: store.getters["session/current_user_is_owner"],
            readonly: store.getters["session/readonly"],
            permissions: store.getters["session/current_user_permissions"] || [],
        }, perm);
    },
    /**
     * Whether the current user holds at least one of the given scope codes.
     * Superusers and entity owners bypass scopes (they have full access, like
     * the backend ``is_owner`` bypass), so they always pass.
     */
    userHasScope(...codes) {
        // While impersonating, ignore the superuser bypass so the menu reflects
        // exactly what the impersonated user can access. The owner bypass already
        // follows the impersonated user (its flag is resolved for them).
        if (store.getters["session/current_user_is_owner"]) {
            return true;
        }
        const userScopes = store.getters["session/current_user_scopes"] || [];
        return codes.some((code) => userScopes.includes(code));
    },
    userCanManageCatalogAssignments() {
        if (store.getters["session/readonly"]) {
            return false;
        }
        const userPermissions = store.getters["session/current_user_permissions"];
        return userCanManageCatalogAssignments(userPermissions);
    },
    userHasProviderOrderPermission(action) {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            PROVIDER_ORDER_PERMISSIONS[action],
            `order.${action}_order`,
            `order.${action}_orderitem`,
        ]);
    },
    userHasCustomerOrderPermission(action) {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            CUSTOMER_ORDER_PERMISSIONS[action],
            `order.${action}_order`,
            `order.${action}_orderitem`,
        ]);
    },
    userHasShipmentPermission(action) {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            `core.${action}_shipment`,
            `core.${action}_shipmentitem`,
        ]);
    },
    userHasReceiptPermission(action) {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            `core.${action}_receipt`,
            `core.${action}_receiptitem`,
        ]);
    },
    userHasOrderItemPermission(action) {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            PROVIDER_ORDER_PERMISSIONS[action],
            CUSTOMER_ORDER_PERMISSIONS[action],
            `order.${action}_order`,
            `order.${action}_orderitem`,
        ]);
    },
    userHasSellCatalogPermission(action) {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            SELL_CATALOG_PERMISSIONS[action],
            `catalog.${action}_catalog`,
            `catalog.${action}_catalogentry`,
            `catalog.${action}_sellcatalogentry`,
        ]);
    },
    userHasProviderCatalogPermission(action) {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            PROVIDER_CATALOG_PERMISSIONS[action],
            `catalog.${action}_catalog`,
            `catalog.${action}_catalogentry`,
            `catalog.${action}_providercatalogentry`,
        ]);
    },
    userHasProviderInvoicePermission(action) {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            PROVIDER_INVOICE_PERMISSIONS[action],
            `billing.${action}_invoice`,
        ]);
    },
    userHasCustomerInvoicePermission(action) {
        const userPermissions = store.getters["session/current_user_permissions"];
        return userHasAnyPermission(userPermissions, [
            CUSTOMER_INVOICE_PERMISSIONS[action],
            `billing.${action}_invoice`,
        ]);
    },
    getCountryName(countryCode) {
        if (!countryCode) return "";
        const key = `country.${countryCode.toLowerCase()}`;
        const translated = Vue.prototype.$translate(key);
        return translated !== key ? translated : countryCode;
    },
    equals(a, b) {
        if (a == null && b == null) return true;
        if (a == b) return true;

        const typeA = typeof a;
        const typeB = typeof b;

        const isNumericLike = (v) =>
            (typeof v === "number" && !isNaN(v)) ||
            (typeof v === "string" && v.trim() !== "" && !isNaN(v));

        if (isNumericLike(a) && isNumericLike(b)) {
            try {
                const decA = new Decimal(a);
                const decB = new Decimal(b);
                return decA.eq(decB);
            } catch {
                return false;
            }
        }

        if (
            (typeA === "boolean" || a === "true" || a === "false") &&
            (typeB === "boolean" || b === "true" || b === "false")
        ) {
            return String(a) === String(b);
        }

        const dateA = new Date(a);
        const dateB = new Date(b);
        if (!isNaN(dateA) && !isNaN(dateB)) {
            return dateA.getTime() === dateB.getTime();
        }

        if (typeA === "object" && typeB === "object") {
            try {
                return JSON.stringify(a) === JSON.stringify(b);
            } catch {
                return false;
            }
        }

        return String(a) === String(b);
    },
    formatNumberLocale(
        value,
        minimumFractionDigits = 2,
        maximumFractionDigits = 10
    ) {
        // Non-numeric values are returned as-is.
        if (typeof value !== "number" || !isFinite(value)) {
            return String(value);
        }

        if (Number.isInteger(value)) {
            return formatNumber(value, { maxDecimals: 0 });
        }

        return formatNumber(value, {
            minDecimals: minimumFractionDigits,
            maxDecimals: maximumFractionDigits,
        });
    },
    /**
     * Format a numeric API value for HTML number inputs (no trailing zeros).
     */
    formatDecimalInputValue(value, maxFractionDigits = 6) {
        if (value === null || value === undefined || value === "") {
            return "";
        }
        const num = Number(value);
        if (!Number.isFinite(num)) {
            return String(value);
        }
        if (Number.isInteger(num)) {
            return String(num);
        }
        return num
            .toFixed(maxFractionDigits)
            .replace(/(\.\d*?)0+$/, "$1")
            .replace(/\.$/, "");
    },
    async downloadFile(blobOrUrlOrConfig, filename = null) {
        // Delegate to the store action
        return store.dispatch("downloads/startDownload", {
            blobOrUrlOrConfig,
            filename,
        });
    },
    downloadInvoicePdf(invoice) {
        if (!invoice || invoice.id == null) {
            return Promise.resolve();
        }
        const invoiceNumber = invoice.invoice_id || invoice.id;
        const filenameKey = invoice.credit ? "creditNotePdf" : "invoicePdf";
        return this.downloadFile(
            `/forms/invoices/${invoice.id}/`,
            this.getFilename(filenameKey, { invoice_number: invoiceNumber })
        );
    },
    extractFilenameFromUrl(url) {
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const filename = pathname.split("/").pop();
            // Remove query parameters if any
            return filename.split("?")[0] || null;
        } catch (e) {
            return null;
        }
    },
    getFilename(key, substitutions = {}) {
        const rawLocale = store.state.session?.active_locale || "fr";
        const locale = rawLocale.split(/[-_]/)[0];
        const localeMessages = messages[locale] || messages.fr;
        let filename = localeMessages?.filenames?.[key] || key;

        // Perform substitutions if provided
        if (substitutions && Object.keys(substitutions).length > 0) {
            Object.keys(substitutions).forEach((placeholder) => {
                let value = substitutions[placeholder];
                if (value !== null && value !== undefined) {
                    // Sanitize value for filename (remove special characters, replace spaces)
                    value = String(value)
                        .replace(/[<>:"/\\|?*]/g, "") // Remove invalid filename characters
                        .replace(/\s+/g, "_") // Replace spaces with underscores
                        .trim();
                    filename = filename.replace(
                        new RegExp(`\\{${placeholder}\\}`, "g"),
                        value
                    );
                }
            });
        }

        return filename;
    },

    /**
     * Format a localized human-readable date range.
     * - If start and end are in the same month/year: "3 – 7 November 2025" (FR: "3 – 7 novembre 2025")
     * - Otherwise: use the localized short format for both ends.
     */
    formatDateRangeLabel(start, end, translate) {
        if (!start || !end) {
            return "";
        }

        const startMoment = Vue.prototype.$moment(start);
        const endMoment = Vue.prototype.$moment(end);

        if (!startMoment.isValid() || !endMoment.isValid()) {
            return "";
        }

        let startLabel;
        let endLabel;

        if (
            startMoment.year() === endMoment.year() &&
            startMoment.month() === endMoment.month()
        ) {
            // Same month & year: only the day for start, full long date (with year) for end
            startLabel = startMoment.format("D");
            endLabel = endMoment.format("LL");
        } else if (startMoment.year() === endMoment.year()) {
            // Same year but different months: abbreviated month, year only on end
            startLabel = startMoment.format("D MMM");
            endLabel = endMoment.format("D MMM YYYY");
        } else {
            startLabel = startMoment.format("ll");
            endLabel = endMoment.format("ll");
        }

        const fromTo = Vue.prototype.$translate("dateRangeFromTo");
        return fromTo.replace("{start}", startLabel).replace("{end}", endLabel);
    },
    /**
     * Format bulk action result for SweetAlert display
     * @param {Object} responseData - The response data with errors and/or success arrays
     * @param {Array} items - The selected items array (to find IDs for error messages)
     * @param {Function} [getIdFn] - Optional function to get the display ID from an item: (item) => string
     * @returns {Object|null} - Object with {title, html, type} for SweetAlert, or null if no errors
     */
    bulkResultToSwalContent(responseData, items, getIdFn) {
        if (!responseData || !responseData.errors || responseData.errors.length === 0) {
            return null;
        }

        const hasErrors = responseData.errors && responseData.errors.length > 0;
        const hasSuccess = responseData.success && responseData.success.length > 0;
        const errorCount = responseData.errors.length;
        
        // Create bulkError function using getIdFn
        const bulkError = (error) => {
            const itemId = error[0];
            const errorMessage = error[1];
            const item = items.find((item) => item.id === itemId);
            const displayId = item && getIdFn ? getIdFn(item) : itemId;
            return displayId + ": " + errorMessage;
        };
        
        const errors = responseData.errors.map((error) => bulkError(error));

        if (hasSuccess) {
            // Partial success - show warning
            return {
                title: Vue.prototype.$translate("warning"),
                html:
                    Vue.prototype.$translate("messages.bulkActionPartiallyCompleted", {
                        errorcount: errorCount,
                    }) +
                    "<br><br>" +
                    "<div style='font-size: 0.9em;'>" +
                    errors.join("<br><br>") +
                    "</div>",
                type: "warning",
            };
        } else {
            // Complete failure - show error
            return {
                title: Vue.prototype.$translate("error"),
                html:
                    Vue.prototype.$translate("messages.bulkActionFailed", {
                        errorcount: errorCount,
                    }) +
                    "<br><br>" +
                    "<div style='font-size: 0.9em;'>" +
                    errors.join("<br><br>") +
                    "</div>",
                type: "error",
            };
        }
    },
      
      /**
       * Get unit options for product units
       * @returns {Array} Array of unit options with text and value
       */
      getUnitOptions(unitType) {
        if (unitType === 'weight') {
            return [
                { text: Vue.prototype.$translate("units.gram"), value: "g" },
                { text: Vue.prototype.$translate("units.kilogram"), value: "kg" },
            ];
        } else if (unitType === 'volume') {
            return [
                { text: Vue.prototype.$translate("units.liter"), value: "L" },
                { text: Vue.prototype.$translate("units.milliliter"), value: "ml" },
                { text: Vue.prototype.$translate("units.centiliter"), value: "cl" },
            ];
        }
        return [];
      },

      getStockUnitOptions() {
        return [
          { text: 'pc', value: 'pc' },
          { text: 'kg', value: 'kg' },
          { text: 'g', value: 'g' },
          { text: 'L', value: 'L' },
          { text: 'cl', value: 'cl' },
          { text: 'ml', value: 'ml' },
        ];
      },

      getPieceContentUnitOptions() {
        return [
          { text: 'kg', value: 'kg' },
          { text: 'g', value: 'g' },
          { text: 'L', value: 'L' },
          { text: 'cl', value: 'cl' },
          { text: 'ml', value: 'ml' },
        ];
      },

      /**
       * Units allowed for bundle component quantity entry on a product.
       */
      getComponentUnitOptions(product) {
        if (!product) {
          return [{ text: 'pc', value: 'pc' }];
        }
        const movementOptions = product.movement_quantity_unit_options;
        if (movementOptions && movementOptions.length) {
          return movementOptions.map((option) => ({
            // Composed display label ("Portion 250 g") disambiguates formats
            // sharing the same container name.
            text: option.format?.name || option.label,
            value: option.value,
            format: option.format || null,
          }));
        }
        const units = product.allowed_component_units;
        if (units && units.length) {
          return units.map((unit) => ({
            text: this.formatComponentUnitLabel(product, unit),
            value: unit,
          }));
        }
        const stockUnit = product.stock_unit || 'pc';
        const cfg = this.getProductQuantityInputConfig(
          stockUnit,
          true
        );
        const allowed = [...cfg.allowedUnits];
        if (
          product.piece_content &&
          stockUnit === 'pc'
        ) {
          const contentCfg = this.getProductQuantityInputConfig(
            product.piece_content.unit,
            true
          );
          contentCfg.allowedUnits.forEach((unit) => {
            if (!allowed.includes(unit)) {
              allowed.push(unit);
            }
          });
        }
        if (
          this.hasDistinctSalePiece(product)
          && !allowed.includes('sale_pc')
        ) {
          allowed.push('sale_pc');
        }
        return allowed.map((unit) => ({
          text: this.formatComponentUnitLabel(product, unit),
          value: unit,
        }));
      },

      formatComponentUnitLabel(product, unit) {
        if (unit === 'sale_pc') {
          const saleFormatLabel = this.getFormatDisplayLabel(
            product?.sale_format_data,
            1,
          );
          if (saleFormatLabel) {
            return saleFormatLabel;
          }
          return product?.format_data?.name || 'pc';
        }
        return unit;
      },

      getStockQuantityUnitOptions(product) {
        if (!product) {
          return [{ text: 'pc', value: 'pc' }];
        }
        const units = product.allowed_component_units;
        if (units && units.length) {
          return units.map((unit) => ({
            text: this.formatComponentUnitLabel(product, unit),
            value: unit,
          }));
        }
        const stockUnit = product.stock_unit || 'pc';
        const cfg = this.getProductQuantityInputConfig(
          stockUnit,
          true,
        );
        return cfg.allowedUnits.map((unit) => ({ text: unit, value: unit }));
      },

      formatCompatibleWithProduct(product, format) {
        if (!product || !format) {
          return false;
        }
        if (
          format.content_quantity == null
          || !format.content_unit
        ) {
          return false;
        }
        const content = {
          qty: new Decimal(format.content_quantity),
          unit: format.content_unit,
        };
        return this.convertEntryContentToStock(product, content, 1) != null;
      },

      /** @deprecated Use formatCompatibleWithProduct */
      saleFormatCompatibleWithProduct(product, saleFormat) {
        return this.formatCompatibleWithProduct(product, saleFormat);
      },

      buildMovementQuantityUnitOptions(product, formats) {
        const seen = new Set();
        const options = [];
        const addOption = (value, label, format = null) => {
          if (!value || seen.has(value)) {
            return;
          }
          seen.add(value);
          options.push({ text: label, value, format });
        };

        this.getStockQuantityUnitOptions(product).forEach(({ text, value }) => {
          addOption(value, text);
        });

        if (!this.productAllowsDecimalQuantity(product)) {
          return options;
        }

        (formats || []).forEach((format) => {
          if (!format?.id || !format.name) {
            return;
          }
          if (!this.formatCompatibleWithProduct(product, format)) {
            return;
          }
          addOption(`sf:${format.id}`, format.name, format);
        });

        return options;
      },

      /**
       * Convert format content to product metadata capacity (cl) or weight (g).
       * Mirrors API content_to_metadata_value.
       */
      contentToMetadataValue(contentQuantity, contentUnit) {
        if (contentQuantity == null || !contentUnit) {
          return null;
        }
        const qty = new Decimal(contentQuantity);
        const volumeToCl = { cl: 1, ml: 0.1, L: 100 };
        const weightToG = { g: 1, kg: 1000 };
        if (volumeToCl[contentUnit] != null) {
          return {
            key: "capacity",
            value: qty.mul(volumeToCl[contentUnit]).toNumber(),
          };
        }
        if (weightToG[contentUnit] != null) {
          return {
            key: "weight",
            value: qty.mul(weightToG[contentUnit]).toNumber(),
          };
        }
        return null;
      },

      /**
       * Join quantity and label, lowercasing the label's first letter after a digit.
       * Mirrors API format_quantity_with_label.
       */
      formatQuantityWithLabel(quantity, label) {
        const qtyText = quantity == null ? "" : String(quantity).trim();
        let labelText = label == null ? "" : String(label).trim();
        if (!labelText) {
          return qtyText;
        }
        // Lowercase after a trailing digit, ignoring locale grouping spaces.
        if (qtyText && /\d[\s\u00a0\u202f]*$/u.test(qtyText)) {
          labelText = labelText.charAt(0).toLowerCase() + labelText.slice(1);
        }
        return `${qtyText} ${labelText}`.trim();
      },

      resolveLocalizedFormatLabel(localized, language = null) {
        if (!localized) {
          return "";
        }
        if (typeof localized === "string") {
          const parsed = this.parseHstoreLocalizedString(localized);
          if (!parsed) {
            return localized;
          }
          localized = parsed;
        }
        const rawLocale = language || store.state.session?.active_locale || "fr";
        const lang = rawLocale.split(/[-_]/)[0];
        let value = localized[lang] || localized.en || localized.fr;
        if (value) {
          const nested = this.parseHstoreLocalizedString(String(value));
          if (nested) {
            return this.resolveLocalizedFormatLabel(nested, lang);
          }
          return String(value);
        }
        const candidates = Object.values(localized);
        for (const candidate of candidates) {
          if (candidate) {
            const nested = this.parseHstoreLocalizedString(String(candidate));
            if (nested) {
              return this.resolveLocalizedFormatLabel(nested, lang);
            }
            return String(candidate);
          }
        }
        return "";
      },

      /**
       * Parse PostgreSQL hstore text ("en"=>"Bottle", "fr"=>"Bouteille").
       */
      parseHstoreLocalizedString(text) {
        if (!text || typeof text !== "string" || !text.includes("=>")) {
          return null;
        }
        const pairs = {};
        const pattern = /"((?:\\.|[^"\\])*)"\s*=>\s*"((?:\\.|[^"\\])*)"/g;
        let match = pattern.exec(text);
        while (match) {
          pairs[match[1].replace(/\\"/g, '"')] = match[2].replace(/\\"/g, '"');
          match = pattern.exec(text);
        }
        return Object.keys(pairs).length ? pairs : null;
      },

      capitalizeFormatLabel(text) {
        if (!text) {
          return text;
        }
        return text.charAt(0).toUpperCase() + text.slice(1);
      },

      /**
       * Capacity/weight strings that may be embedded in a format label.
       * Mirrors API content_display_variants (spaced and unspaced, L/kg norms).
       */
      contentDisplayVariants(contentQuantity, contentUnit) {
        if (contentQuantity == null || !contentUnit) {
          return [];
        }
        let qty;
        try {
          qty = new Decimal(String(contentQuantity));
        } catch {
          return [];
        }
        const unit = String(contentUnit).trim();
        if (!unit) {
          return [];
        }
        const variants = new Set();
        const add = (amount, unitLabel) => {
          let raw;
          try {
            const normalized = new Decimal(String(amount));
            raw = normalized.eq(normalized.toDecimalPlaces(0))
              ? String(normalized.toDecimalPlaces(0).toNumber())
              : this.formatDecimalInputValue(normalized, 3);
          } catch {
            raw = String(amount);
          }
          const rawDot = raw.replace(",", ".");
          for (const amountText of new Set([raw, rawDot])) {
            variants.add(`${amountText} ${unitLabel}`);
            variants.add(`${amountText}${unitLabel}`);
            variants.add(`${amountText} ${unitLabel.toUpperCase()}`);
            variants.add(`${amountText} ${unitLabel.toLowerCase()}`);
            variants.add(`${amountText}${unitLabel.toUpperCase()}`);
            variants.add(`${amountText}${unitLabel.toLowerCase()}`);
          }
        };
        add(qty, unit);
        if (unit === "cl" && qty.gte(100) && qty.mod(100).eq(0)) {
          add(qty.div(100), "L");
          add(qty.div(100), "l");
        }
        if (unit === "ml" && qty.gte(1000) && qty.mod(1000).eq(0)) {
          add(qty.div(1000), "L");
          add(qty.div(1000), "l");
        }
        if (unit === "g" && qty.gte(1000) && qty.mod(1000).eq(0)) {
          add(qty.div(1000), "kg");
          add(qty.div(1000), "Kg");
        }
        return [...variants].sort((a, b) => b.length - a.length);
      },

      /**
       * Remove a matching capacity/weight suffix from a format label.
       * Mirrors API strip_matching_capacity_from_label.
       */
      stripCapacityFromFormatLabel(label, formatData) {
        const text = String(label || "").trim();
        if (!text || !formatData) {
          return text;
        }
        const lower = text.toLowerCase();
        const candidates = this.contentDisplayVariants(
          formatData.content_quantity,
          formatData.content_unit,
        );
        for (const candidate of candidates) {
          const needle = String(candidate || "")
            .toLowerCase()
            .trim();
          if (!needle) {
            continue;
          }
          if (lower.endsWith(needle)) {
            const stripped = text
              .slice(0, text.length - needle.length)
              .replace(/[\s\-–—]+$/u, "")
              .trim();
            return stripped || text;
          }
          if (
            lower.startsWith(`${needle} `)
            || lower.startsWith(`${needle}-`)
          ) {
            const stripped = text
              .slice(needle.length)
              .replace(/^[\s\-–—]+/u, "")
              .trim();
            return stripped || text;
          }
        }
        return text;
      },

      /**
       * Singular/plural Format label. Mirrors API get_format_label.
       */
      getFormatLabel(formatData, quantity = 1, language = null) {
        if (!formatData) {
          return "";
        }
        let qty;
        try {
          qty = new Decimal(String(quantity)).abs();
        } catch {
          qty = new Decimal(1);
        }
        const usePlural = qty.gt(1);
        let label = this.resolveLocalizedFormatLabel(
          usePlural ? formatData.label_plural : formatData.label_singular,
          language,
        );
        if (!label && usePlural) {
          label = this.resolveLocalizedFormatLabel(
            formatData.label_singular,
            language,
          );
        }
        if (!label) {
          label =
            formatData.container_label
            || this.getFormatGroupLabel(formatData)
            || this.stripCapacityFromFormatLabel(formatData.name, formatData);
        }
        // Labels may still embed capacity ("palette 350 kg") — strip before
        // composing with content so plurals stay clean ("palettes" + "350 kg").
        label = this.stripCapacityFromFormatLabel(label, formatData) || label;
        return this.capitalizeFormatLabel(label);
      },

      /**
       * Quantity + format display label (e.g. "6 verres 12 cl").
       * Mirrors API get_format_quantity_label.
       */
      getFormatQuantityLabel(formatData, quantity, language = null) {
        const label = this.getFormatDisplayLabel(formatData, quantity, language);
        let qtyDisplay = quantity;
        try {
          const qty = new Decimal(String(quantity));
          qtyDisplay = qty.eq(qty.toDecimalPlaces(0))
            ? qty.toNumber()
            : qty.toString();
        } catch {
          // keep original value
        }
        // Pass the raw numeric display into formatQuantityWithLabel so the
        // label's first letter is lowercased after the digit (not after a
        // locale-formatted quantity that might not end with \d).
        const qtyText = formatQuantity(qtyDisplay);
        return this.formatQuantityWithLabel(qtyText, label);
      },

      /**
       * Pick singular/plural label from a snapshotted transactional line.
       */
      resolveSnapshottedSaleUnitLabel(item, quantity) {
        const singular = (item?.sale_unit_label || "").trim();
        const plural = (item?.sale_unit_label_plural || "").trim();
        if (!singular || singular === "pc") {
          return null;
        }
        let qty;
        try {
          qty = new Decimal(String(quantity)).abs();
        } catch {
          qty = new Decimal(1);
        }
        if (qty.gt(1) && plural) {
          return plural;
        }
        return singular;
      },

      /**
       * Normalized unit snapshot for a logistics line (shipment/receipt item).
       */
      logisticsUnitFields(line) {
        return {
          unit: line?.unit || null,
          unit_label: line?.unit_label || null,
          unit_label_plural: line?.unit_label_plural || null,
          content_quantity: line?.content_quantity ?? null,
          content_unit: line?.content_unit || null,
        };
      },

      /**
       * Display snapshot of a movement mapped to the logistics unit shape.
       * Returns null when the movement has no usable display snapshot
       * (legacy rows, or incomplete POS rows stored as quantity 0 with no unit).
       */
      movementDisplayFields(movement) {
        if (movement?.display_quantity == null || movement.display_quantity === "") {
          return null;
        }
        const fields = {
          quantity: movement.display_quantity,
          unit: movement.display_unit || null,
          unit_label: movement.display_unit_label || null,
          unit_label_plural: movement.display_unit_label_plural || null,
          content_quantity: movement.display_content_quantity ?? null,
          content_unit: movement.display_content_unit || null,
        };
        // QuantityField defaulted to 0 when POS import omitted the snapshot.
        try {
          const qty = new Decimal(String(fields.quantity));
          const contentQty =
            fields.content_quantity == null || fields.content_quantity === ""
              ? null
              : new Decimal(String(fields.content_quantity));
          if (
            qty.isZero()
            && !(fields.unit_label || "").trim()
            && (contentQty == null || contentQty.isZero())
            && !(fields.content_unit || "").trim()
          ) {
            return null;
          }
        } catch {
          // keep fields
        }
        return fields;
      },

      /**
       * True when the unit snapshot is the identity mapping (1:1 stock unit):
       * no custom label and content = 1 × same unit.
       */
      logisticsUnitIsIdentity(fields) {
        if (!fields) {
          return true;
        }
        if ((fields.unit_label || "").trim()) {
          return false;
        }
        if (fields.content_quantity != null && fields.content_quantity !== "") {
          try {
            if (!new Decimal(String(fields.content_quantity)).eq(1)) {
              return false;
            }
          } catch {
            // treat unparsable content as identity
          }
        }
        return !fields.content_unit || fields.content_unit === fields.unit;
      },

      /**
       * Format a logistics quantity with its snapshotted unit label
       * (e.g. "12 cagettes 17,5 kg", "3,5 kg", "8"). The piece unit is implied.
       */
      formatLogisticsQuantity(fields, quantity) {
        if (quantity == null || quantity === "") {
          return "";
        }
        const qtyText = formatQuantity(quantity);
        if (!fields) {
          return qtyText;
        }
        let label = this.resolveSnapshottedSaleUnitLabel(
          {
            sale_unit_label: fields.unit_label,
            sale_unit_label_plural: fields.unit_label_plural,
          },
          quantity,
        );
        if (label) {
          label =
            this.appendContentToSaleFormatLabel(label, {
              sale_content_quantity: fields.content_quantity,
              sale_content_unit: fields.content_unit,
              content_quantity: fields.content_quantity,
              content_unit: fields.content_unit,
            }) || label;
        }
        if (!label) {
          label = fields.unit && fields.unit !== "pc" ? fields.unit : "";
        }
        return label
          ? this.formatQuantityWithLabel(qtyText, label)
          : qtyText;
      },

      /**
       * Format a shipment/receipt line quantity (defaults to line.count) with
       * its snapshotted unit.
       */
      formatLineQuantity(line, quantity = undefined) {
        const qty = quantity === undefined ? line?.count : quantity;
        return this.formatLogisticsQuantity(this.logisticsUnitFields(line), qty);
      },

      /**
       * Prepared quantity shown on operator/readonly shipment lines.
       * Mirrors API ``shipmentitem_prepared_logistics_quantity``: missing → 0;
       * falsy / unset ``prepared_count`` falls back to ``count`` (delivered lines
       * often keep a null snapshot after receipt completion).
       */
      shipmentItemPreparedDisplayQuantity(line) {
        if (!line) {
          return 0;
        }
        if (line.status === "missing") {
          return 0;
        }
        const prepared = line.prepared_count;
        if (prepared != null && prepared !== "") {
          try {
            if (!new Decimal(String(prepared)).isZero()) {
              return prepared;
            }
          } catch {
            if (Number(prepared) !== 0) {
              return prepared;
            }
          }
        }
        return line.count ?? 0;
      },

      /**
       * True when the current quantity differs from the original shipped quantity.
       */
      isShipmentItemQuantityChanged(line) {
        if (!line || line.initial_count == null || line.initial_count === "") {
          return false;
        }
        try {
          return (
            new Decimal(String(line.count ?? 0)).comparedTo(
              new Decimal(String(line.initial_count))
            ) !== 0
          );
        } catch {
          return Number(line.count) !== Number(line.initial_count);
        }
      },

      /**
       * Sale format label for a transactional line (order/invoice/shipment).
       * Prefer the snapshotted line label so later product/catalog edits do not
       * rewrite historical display. Contenance is appended when available
       * (mirrors API get_line_sale_format_display_label).
       */
      getOrderItemSaleFormatLabel(item) {
        if (!item) {
          return null;
        }
        const snapshotLabel = (item.sale_unit_label || "").trim();
        if (snapshotLabel && snapshotLabel !== "pc") {
          return this.appendContentToSaleFormatLabel(snapshotLabel, item) || snapshotLabel;
        }
        if (
          item.sale_content_quantity != null
          && item.sale_content_quantity !== ""
          && item.sale_content_unit
        ) {
          try {
            const qty = new Decimal(String(item.sale_content_quantity))
              .toDecimalPlaces(3)
              .toString();
            return `${qty} ${item.sale_content_unit}`;
          } catch {
            return `${item.sale_content_quantity} ${item.sale_content_unit}`;
          }
        }
        // Legacy lines without a snapshot: fall back to live catalog/product.
        return this.formatCatalogEntrySaleContent(item.catalogentry_data);
      },

      /**
       * Format metadata for plural-aware sale quantity when the line uses a Format.
       * Returns null when the line already has a snapshotted label — live
       * catalogentry.format_data must not override historical display.
       */
      getOrderItemSaleFormatData(item) {
        // Any line-level sale snapshot means display must stay frozen.
        if ((item?.sale_unit_label || "").trim()) {
          return null;
        }
        if (
          item?.sale_content_quantity != null
          && item?.sale_content_quantity !== ""
          && item?.sale_content_unit
        ) {
          return null;
        }
        const entry = item?.catalogentry_data;
        if (!entry?.format_data) {
          return null;
        }
        if (entry.format) {
          return entry.format_data;
        }
        const product = entry.product_data;
        if (
          product?.sale_unit_content_quantity != null
          && product?.sale_unit
          && product?.stock_unit
          && product.sale_unit !== product.stock_unit
        ) {
          return null;
        }
        if (this.getCatalogEntrySaleContent(entry)) {
          return entry.format_data;
        }
        return null;
      },

      formatComponentQuantity(quantity, unit) {
        if (quantity === null || quantity === undefined || quantity === '') {
          return '-';
        }
        return formatQuantity(quantity, { unit: unit || '' });
      },

      getUnitTypeOptions() {
        return [
          { text: Vue.prototype.$translate("unitTypes.weight"), value: "weight" },
          { text: Vue.prototype.$translate("unitTypes.volume"), value: "volume" },
          { text: Vue.prototype.$translate("unitTypes.piece"), value: "piece" },
        ];
      },

      /**
       * Get short unit options for a specific unit type
       * @param {string} unitType - The unit type (weight, volume, unit)
       * @returns {Array} Array of short unit options with text and value
       */
      getShortUnitOptions(unitType) {
        if (unitType === 'weight') {
          return [
            { text: "g", value: "g" },
            { text: "kg", value: "kg" },
          ];
        } else if (unitType === 'volume') {
          return [
            { text: "ml", value: "ml" },
            { text: "cl", value: "cl" },
            { text: "L", value: "L" },
          ];
        }
        return [];
      },

      /**
       * Whether a product accepts decimal stock quantities (always true).
       */
      parseStockLevelQuantity(value) {
        if (value === null || value === undefined || value === '') {
          return null;
        }
        const qty = Number(value);
        return Number.isFinite(qty) ? qty : null;
      },

      getProductLowStockThreshold(product) {
        if (!product) {
          return 0;
        }
        const threshold =
          product.low_stock_alert_treshold
          ?? product.product_data?.low_stock_alert_treshold
          ?? product.child_product_data?.low_stock_alert_treshold;
        if (threshold === null || threshold === undefined || threshold === '') {
          return 0;
        }
        return threshold;
      },

      /**
       * Normalize low-stock threshold input: empty means 0.
       */
      normalizeLowStockThreshold(value) {
        const normalized = this.normalizeDecimalString(value);
        if (normalized === null || normalized === undefined || normalized === '') {
          return 0;
        }
        return normalized;
      },

      /**
       * Stock level severity for UI coloring.
       * negative: count < 0 (red), low: at or below alert threshold (orange).
       */
      getStockLevelSeverity(count, lowStockThreshold) {
        const qty = this.parseStockLevelQuantity(count);
        if (qty === null) {
          return null;
        }
        if (qty < 0) {
          return 'negative';
        }
        const threshold = this.parseStockLevelQuantity(lowStockThreshold);
        if (threshold !== null && qty <= threshold) {
          return 'low';
        }
        return null;
      },

      getStockLevelTextClass(count, lowStockThreshold) {
        const severity = this.getStockLevelSeverity(count, lowStockThreshold);
        if (severity === 'negative') {
          return 'error--text font-weight-bold';
        }
        if (severity === 'low') {
          return 'warning--text font-weight-bold';
        }
        return '';
      },

      getProductStockLevelTextClass(product, count) {
        const stockCount =
          count !== undefined && count !== null
            ? count
            : product?.stock_count ?? product?.stockproduct_data?.count;
        return this.getStockLevelTextClass(
          stockCount,
          this.getProductLowStockThreshold(product),
        );
      },

      isStockLevelAlert(count, lowStockThreshold) {
        const severity = this.getStockLevelSeverity(count, lowStockThreshold);
        return severity === 'negative' || severity === 'low';
      },

      productAllowsDecimalQuantity(product) {
        return true;
      },

      getSaleUnit(product) {
        if (!product) {
          return 'pc';
        }
        return product.sale_unit || product.stock_unit || 'pc';
      },

      saleUnitAllowsDecimalQuantity(product) {
        return true;
      },

      hasDistinctSalePiece(product) {
        // Sale and stock units are both pieces but a sale unit content is
        // defined: the sale piece differs from the stock piece
        // (e.g. a 12 cl glass sold out of a 75 cl bottle stock).
        if (!product || product.sale_unit !== 'pc') {
          return false;
        }
        if ((product.stock_unit || 'pc') !== 'pc') {
          return false;
        }
        const contentQty = parseFloat(product.sale_unit_content_quantity);
        return contentQty > 0 && !!product.sale_unit_content_unit;
      },

      getSaleQuantityInputConfig(product) {
        if (!product) {
          return this.getProductQuantityInputConfig('pc', false);
        }
        const saleUnit = this.getSaleUnit(product);
        const stockUnit = product.stock_unit || 'pc';
        if (saleUnit === stockUnit) {
          if (this.hasDistinctSalePiece(product)) {
            return {
              unitType: 'piece',
              allowedUnits: ['pc'],
              allowDecimalQuantity: this.saleUnitAllowsDecimalQuantity(product),
            };
          }
          if (saleUnit === 'pc') {
            const saleDecimal = this.saleUnitAllowsDecimalQuantity(product);
            if (
              saleDecimal
              && product.piece_content
            ) {
              return this.getProductQuantityInputConfig(
                product.piece_content.unit,
                true
              );
            }
            return {
              unitType: 'piece',
              allowedUnits: ['pc'],
              allowDecimalQuantity: saleDecimal,
            };
          }
          return this.getProductQuantityInputConfig(
            stockUnit,
            true
          );
        }
        if (saleUnit === 'pc') {
          return {
            unitType: 'piece',
            allowedUnits: ['pc'],
            allowDecimalQuantity: this.saleUnitAllowsDecimalQuantity(product),
          };
        }
        return this.getProductQuantityInputConfig(
          saleUnit,
          this.saleUnitAllowsDecimalQuantity(product)
        );
      },

      /**
       * Dimension of a unit code: 'weight' (kg/g), 'volume' (L/cl/ml) or 'piece'.
       */
      getUnitDimension(unit) {
        if (unit === 'kg' || unit === 'g') {
          return 'weight';
        }
        if (unit === 'L' || unit === 'cl' || unit === 'ml') {
          return 'volume';
        }
        return 'piece';
      },

      getFormatGroupLabel(format) {
        return cascadeContainerLabel(format);
      },

      getFormatGroupKey(format) {
        return cascadeContainerKey(format);
      },

      getFormatContainerKey(format) {
        return cascadeContainerKey(format);
      },

      getFormatContainerLabel(format) {
        return cascadeContainerLabel(format);
      },

      formatsForContainer(formats, containerKey) {
        return cascadeFormatsForContainer(formats, containerKey);
      },

      unitsForContainer(formats, containerKey) {
        return cascadeUnitsForContainer(formats, containerKey);
      },

      quantitiesForContainerAndUnit(formats, containerKey, unit) {
        return cascadeQuantitiesForContainerAndUnit(
          formats,
          containerKey,
          unit,
        );
      },

      resolveFormat(formats, containerKey, quantity, unit) {
        return cascadeResolveFormat(formats, containerKey, quantity, unit);
      },

      buildFormatContainerOptions(formats = []) {
        const sorted = this.sortFormatsByCapacity(formats);
        return cascadeBuildContainerOptions(sorted);
      },

      getFormatCapacitySortKey(format) {
        if (!format?.content_quantity || !format?.content_unit) {
          return { tier: 2, amount: Number.POSITIVE_INFINITY };
        }
        const dimension = this.getUnitDimension(format.content_unit);
        if (dimension === 'volume') {
          const inCl = this.convertUnitQuantity(
            format.content_quantity,
            format.content_unit,
            'cl',
          );
          return {
            tier: 0,
            amount: inCl ? inCl.toNumber() : Number.POSITIVE_INFINITY,
          };
        }
        if (dimension === 'weight') {
          const inG = this.convertUnitQuantity(
            format.content_quantity,
            format.content_unit,
            'g',
          );
          return {
            tier: 1,
            amount: inG ? inG.toNumber() : Number.POSITIVE_INFINITY,
          };
        }
        return { tier: 2, amount: Number.POSITIVE_INFINITY };
      },

      compareFormatsByCapacity(formatA, formatB) {
        const groupCmp = this.getFormatGroupKey(formatA).localeCompare(
          this.getFormatGroupKey(formatB),
          undefined,
          { sensitivity: 'base' },
        );
        if (groupCmp !== 0) {
          return groupCmp;
        }
        const keyA = this.getFormatCapacitySortKey(formatA);
        const keyB = this.getFormatCapacitySortKey(formatB);
        if (keyA.tier !== keyB.tier) {
          return keyA.tier - keyB.tier;
        }
        if (keyA.amount !== keyB.amount) {
          return keyA.amount - keyB.amount;
        }
        return (formatA?.name || '').localeCompare(
          formatB?.name || '',
          undefined,
          { numeric: true, sensitivity: 'base' },
        );
      },

      sortFormatsByCapacity(formats) {
        return [...(formats || [])].sort((a, b) =>
          this.compareFormatsByCapacity(a, b),
        );
      },

      formatMatchesCapacityQuery(format, query) {
        const text = String(query || '').trim().toLowerCase();
        if (!text || !format?.content_quantity || !format?.content_unit) {
          return false;
        }
        const numberMatch = text.match(/(\d+(?:[.,]\d+)?)/);
        if (!numberMatch) {
          return false;
        }
        const amount = parseFloat(numberMatch[1].replace(',', '.'));
        if (Number.isNaN(amount)) {
          return false;
        }
        let queryUnit = null;
        if (/\bml\b/.test(text) || /\dml(?:\b|$)/.test(text)) {
          queryUnit = 'ml';
        } else if (/\bcl\b/.test(text) || /\dcl(?:\b|$)/.test(text)) {
          queryUnit = 'cl';
        } else if (/\bkg\b/.test(text) || /\dkg(?:\b|$)/.test(text)) {
          queryUnit = 'kg';
        } else if (/(?:^|\s|\d)g(?:$|\s)/.test(text)) {
          queryUnit = 'g';
        } else if (/(?:^|\s|\d)l(?:$|\s)/.test(text) || /\d+l\b/.test(text)) {
          queryUnit = 'L';
        }
        const formatQty = parseFloat(format.content_quantity);
        const formatUnit = format.content_unit;
        if (queryUnit) {
          const converted = this.convertUnitQuantity(
            formatQty,
            formatUnit,
            queryUnit,
          );
          return converted
            ? Math.abs(converted.toNumber() - amount) < 0.001
            : false;
        }
        if (Math.abs(formatQty - amount) < 0.001) {
          return true;
        }
        const dimension = this.getUnitDimension(formatUnit);
        if (dimension === 'volume') {
          const inCl = this.convertUnitQuantity(formatQty, formatUnit, 'cl');
          if (inCl && Math.abs(inCl.toNumber() - amount) < 0.001) {
            return true;
          }
        }
        if (dimension === 'weight') {
          const inG = this.convertUnitQuantity(formatQty, formatUnit, 'g');
          if (inG && Math.abs(inG.toNumber() - amount) < 0.001) {
            return true;
          }
        }
        return false;
      },

      /**
       * Convert a quantity between two units of the same dimension.
       * Returns a Decimal, or null when the units are not compatible.
       */
      convertUnitQuantity(qty, fromUnit, toUnit) {
        if (fromUnit === toUnit) {
          return new Decimal(qty);
        }
        const factors = { kg: 1, g: 0.001, L: 1, cl: 0.01, ml: 0.001 };
        if (
          this.getUnitDimension(fromUnit) !== this.getUnitDimension(toUnit)
          || !(fromUnit in factors)
          || !(toUnit in factors)
        ) {
          return null;
        }
        return new Decimal(qty).mul(factors[fromUnit]).div(factors[toUnit]);
      },

      /** Content of one sale unit as { qty: Decimal, unit } or null. */
      getSaleUnitContent(product) {
        const rawQty = product?.sale_unit_content_quantity;
        const unit = product?.sale_unit_content_unit;
        if (rawQty == null || rawQty === '' || !unit) {
          return null;
        }
        const qty = new Decimal(rawQty);
        return qty.gt(0) ? { qty, unit } : null;
      },

      /** Content of one stock piece as { qty: Decimal, unit } or null. */
      getPieceContent(product) {
        const content = product?.piece_content;
        const rawQty = content?.quantity;
        if (rawQty == null || rawQty === '' || !content?.unit) {
          return null;
        }
        const qty = new Decimal(rawQty);
        return qty.gt(0) ? { qty, unit: content.unit } : null;
      },

      /**
       * Convert between sale unit and stock unit through the sale unit content.
       * Mirror of the backend _convert_via_sale_unit_content; returns a Decimal
       * or null when the conversion is not determinable.
       */
      convertViaSaleUnitContent(product, qty, toStock) {
        const content = this.getSaleUnitContent(product);
        if (!content) {
          return null;
        }
        const stockUnit = product.stock_unit || 'pc';
        const contentDimension = this.getUnitDimension(content.unit);
        const stockDimension = this.getUnitDimension(stockUnit);
        const quantity = new Decimal(qty);

        if (toStock) {
          const qtyInContent = quantity.mul(content.qty);
          if (content.unit === stockUnit) {
            return qtyInContent;
          }
          if (contentDimension === stockDimension) {
            return this.convertUnitQuantity(qtyInContent, content.unit, stockUnit);
          }
          // Stock unit is a piece with its own content (e.g. glasses -> bottles).
          const pieceContent = this.getPieceContent(product);
          if (!pieceContent) {
            return null;
          }
          const qtyInPieceUnit = this.convertUnitQuantity(
            qtyInContent, content.unit, pieceContent.unit
          );
          return qtyInPieceUnit ? qtyInPieceUnit.div(pieceContent.qty) : null;
        }

        if (content.unit === stockUnit) {
          return quantity.div(content.qty);
        }
        if (contentDimension === stockDimension) {
          const qtyInContent = this.convertUnitQuantity(quantity, stockUnit, content.unit);
          return qtyInContent ? qtyInContent.div(content.qty) : null;
        }
        // Stock unit is a piece with its own content (e.g. bottles -> glasses).
        const pieceContent = this.getPieceContent(product);
        if (!pieceContent) {
          return null;
        }
        const qtyInContent = this.convertUnitQuantity(
          quantity.mul(pieceContent.qty), pieceContent.unit, content.unit
        );
        return qtyInContent ? qtyInContent.div(content.qty) : null;
      },

      convertSaleToStockQuantity(product, saleQty) {
        if (saleQty == null || saleQty === '') {
          return null;
        }
        if (!product) {
          return Number(saleQty);
        }
        const stockUnit = product.stock_unit || 'pc';
        const saleUnit = this.getSaleUnit(product);
        let result;
        if (saleUnit === stockUnit) {
          if (this.hasDistinctSalePiece(product)) {
            result = this.convertViaSaleUnitContent(product, saleQty, true);
          } else {
            result = new Decimal(saleQty);
          }
        } else {
          const saleDimension = this.getUnitDimension(saleUnit);
          const stockDimension = this.getUnitDimension(stockUnit);
          if (saleDimension === stockDimension) {
            result = this.convertUnitQuantity(saleQty, saleUnit, stockUnit);
          } else if (saleUnit === 'pc') {
            result = this.convertViaSaleUnitContent(product, saleQty, true);
          } else if (stockUnit === 'pc') {
            // Sale unit is weight/volume, stock is pieces with a content.
            const pieceContent = this.getPieceContent(product);
            const qtyInPieceUnit = pieceContent
              ? this.convertUnitQuantity(saleQty, saleUnit, pieceContent.unit)
              : null;
            result = qtyInPieceUnit ? qtyInPieceUnit.div(pieceContent.qty) : null;
          } else {
            result = null;
          }
        }
        return result ? result.toDecimalPlaces(3).toNumber() : null;
      },

      /**
       * Convert a stock-unit quantity to the product sale unit.
       * Mirror of the backend convert_stock_to_sale; returns a number rounded
       * to 3 decimals, or null when the conversion is not determinable.
       */
      convertStockToSaleQuantity(product, stockQty) {
        if (stockQty == null || stockQty === '') {
          return null;
        }
        if (!product) {
          return Number(stockQty);
        }
        const stockUnit = product.stock_unit || 'pc';
        const saleUnit = this.getSaleUnit(product);
        let result;
        if (saleUnit === stockUnit) {
          if (this.hasDistinctSalePiece(product)) {
            result = this.convertViaSaleUnitContent(product, stockQty, false);
          } else {
            result = new Decimal(stockQty);
          }
        } else {
          const saleDimension = this.getUnitDimension(saleUnit);
          const stockDimension = this.getUnitDimension(stockUnit);
          if (saleDimension === stockDimension) {
            result = this.convertUnitQuantity(stockQty, stockUnit, saleUnit);
          } else if (saleUnit === 'pc') {
            result = this.convertViaSaleUnitContent(product, stockQty, false);
          } else if (stockUnit === 'pc') {
            // Stock is pieces with a content, sale unit is weight/volume.
            const pieceContent = this.getPieceContent(product);
            const qtyInSaleUnit = pieceContent
              ? this.convertUnitQuantity(
                new Decimal(stockQty).mul(pieceContent.qty),
                pieceContent.unit,
                saleUnit
              )
              : null;
            result = qtyInSaleUnit;
          } else {
            result = null;
          }
        }
        return result ? result.toDecimalPlaces(3).toNumber() : null;
      },

      getCatalogEntrySaleContent(catalogentry) {
        if (!catalogentry) {
          return null;
        }
        const formatData = catalogentry.format_data || catalogentry.sale_format_data;
        if (formatData) {
          return {
            qty: new Decimal(formatData.content_quantity),
            unit: formatData.content_unit,
          };
        }
        const display = catalogentry.sale_content_display;
        if (display?.quantity != null && display?.unit) {
          return {
            qty: new Decimal(display.quantity),
            unit: display.unit,
          };
        }
        if (
          catalogentry.sale_content_quantity != null
          && catalogentry.sale_content_unit
        ) {
          return {
            qty: new Decimal(catalogentry.sale_content_quantity),
            unit: catalogentry.sale_content_unit,
          };
        }
        return null;
      },

      catalogEntryHasSaleContent(catalogentry) {
        return this.getCatalogEntrySaleContent(catalogentry) != null;
      },

      buildPreviewCatalogEntryForAddToCatalog(product, addFormatId, formats) {
        const selectedFormat = (formats || []).find(
          (item) => item.id === addFormatId,
        );
        return {
          product_data: product,
          format: addFormatId || null,
          format_data: selectedFormat || null,
          sale_content_quantity: null,
          sale_content_unit: null,
          sale_content_display: selectedFormat
            ? {
                quantity: selectedFormat.content_quantity,
                unit: selectedFormat.content_unit,
              }
            : null,
        };
      },

      productNeedsPieceContentForAddToCatalog(product, addFormatId, formats) {
        const preview = this.buildPreviewCatalogEntryForAddToCatalog(
          product,
          addFormatId,
          formats,
        );
        return this.catalogEntryNeedsPieceContentForConversion(preview);
      },

      buildPreviewCatalogEntryWithPieceContent(
        product,
        addFormatId,
        formats,
        pieceContentForm,
      ) {
        const preview = this.buildPreviewCatalogEntryForAddToCatalog(
          product,
          addFormatId,
          formats,
        );
        const qty = pieceContentForm?.quantity;
        const unit = pieceContentForm?.unit;
        if (!qty || !unit || Number(qty) <= 0) {
          return preview;
        }
        return {
          ...preview,
          product_data: {
            ...product,
            piece_content: {
              quantity: qty,
              unit,
            },
            piece_content_quantity: qty,
            piece_content_unit: unit,
          },
        };
      },

      /**
       * True when a catalog entry sale unit converts to stock pieces but the
       * product is missing piece content metadata (e.g. 75 cl per bottle).
       */
      catalogEntryNeedsPieceContentForConversion(catalogentry) {
        if (!this.catalogEntryHasSaleContent(catalogentry)) {
          return false;
        }
        const product = catalogentry?.product_data;
        if (!product) {
          return false;
        }
        const stockUnit = product.stock_unit || 'pc';
        if (stockUnit !== 'pc') {
          return false;
        }
        const content = this.getCatalogEntrySaleContent(catalogentry);
        if (!content) {
          return false;
        }
        const contentDimension = this.getUnitDimension(content.unit);
        if (!['weight', 'volume'].includes(contentDimension)) {
          return false;
        }
        return this.getPieceContent(product) == null;
      },

      catalogEntryCanConvertSaleToStock(catalogentry, entryQty = 1) {
        if (!this.catalogEntryHasSaleContent(catalogentry)) {
          return true;
        }
        return this.convertEntrySaleToStockQuantity(catalogentry, entryQty) != null;
      },

      /**
       * Unit content of one stock piece from piece_content fields,
       * dedicated product fields, or the product format.
       */
      getProductUnitContent(product) {
        const fromPieceContent = this.getPieceContent(product);
        if (fromPieceContent) {
          return fromPieceContent;
        }
        const rawQty = product?.piece_content_quantity;
        const unit = product?.piece_content_unit;
        if (rawQty != null && rawQty !== '' && unit) {
          const qty = new Decimal(rawQty);
          if (qty.gt(0)) {
            return { qty, unit };
          }
        }
        const formatData = product?.format_data;
        if (
          formatData?.content_quantity != null
          && formatData?.content_unit
        ) {
          const qty = new Decimal(formatData.content_quantity);
          if (qty.gt(0)) {
            return { qty, unit: formatData.content_unit };
          }
        }
        return null;
      },

      getAddToCatalogSaleFormatDimension(addFormatId, formats) {
        const selectedFormat = (formats || []).find(
          (item) => item.id === addFormatId,
        );
        if (!selectedFormat?.content_unit) {
          return null;
        }
        const dimension = this.getUnitDimension(selectedFormat.content_unit);
        return ['weight', 'volume'].includes(dimension) ? dimension : null;
      },

      getPieceContentUnitOptionsForAddToCatalogSaleFormat(addFormatId, formats) {
        const options = this.getPieceContentUnitOptions();
        const saleDimension = this.getAddToCatalogSaleFormatDimension(
          addFormatId,
          formats,
        );
        if (!saleDimension) {
          return options;
        }
        return options.filter(
          (option) => this.getUnitDimension(option.value) === saleDimension,
        );
      },

      pieceContentFormCompatibleWithAddToCatalogSaleFormat(
        product,
        addFormatId,
        formats,
        pieceContentForm,
      ) {
        const preview = this.buildPreviewCatalogEntryForAddToCatalog(
          product,
          addFormatId,
          formats,
        );
        if (!this.catalogEntryHasSaleContent(preview)) {
          return true;
        }
        let quantity = pieceContentForm?.quantity;
        let unit = pieceContentForm?.unit;
        if (
          (!quantity || !unit)
          && pieceContentForm?.product_format_id
          && pieceContentForm.product_format_id !== FORMAT_SELECT_CUSTOM
        ) {
          const unitFormat = (formats || []).find(
            (entry) => entry.id === pieceContentForm.product_format_id,
          );
          if (unitFormat?.content_quantity != null && unitFormat?.content_unit) {
            quantity = this.formatDecimalInputValue(
              unitFormat.content_quantity,
              3,
            );
            unit = unitFormat.content_unit;
          }
        }
        if (!quantity || !unit || Number(quantity) <= 0) {
          return true;
        }
        const withPieceContent = this.buildPreviewCatalogEntryWithPieceContent(
          product,
          addFormatId,
          formats,
          {
            ...pieceContentForm,
            quantity,
            unit,
          },
        );
        return this.catalogEntryCanConvertSaleToStock(withPieceContent, 1);
      },

      unitFormatCompatibleWithAddToCatalogSaleFormat(
        product,
        addFormatId,
        formats,
        unitFormat,
      ) {
        if (!unitFormat?.content_quantity || !unitFormat?.content_unit) {
          return false;
        }
        return this.pieceContentFormCompatibleWithAddToCatalogSaleFormat(
          product,
          addFormatId,
          formats,
          {
            quantity: this.formatDecimalInputValue(
              unitFormat.content_quantity,
              3,
            ),
            unit: unitFormat.content_unit,
          },
        );
      },

      getCatalogEntryPurchasePriceExclVat(catalogentry) {
        if (!catalogentry) {
          return null;
        }
        if (
          catalogentry.unit_purchase_price_excl_vat !== null
          && catalogentry.unit_purchase_price_excl_vat !== undefined
        ) {
          return catalogentry.unit_purchase_price_excl_vat;
        }
        const product = catalogentry.product_data;
        if (!product) {
          return null;
        }
        let purchasePrice = (
          product.unit_purchase_price_excl_vat
          ?? product.default_unit_purchase_price_excl_vat
        );
        if (purchasePrice == null) {
          return null;
        }
        if (this.catalogEntryHasSaleContent(catalogentry)) {
          const stockRatio = this.convertEntrySaleToStockQuantity(catalogentry, 1);
          if (stockRatio != null) {
            purchasePrice = new Decimal(purchasePrice)
              .mul(stockRatio)
              .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
              .toNumber();
          }
        }
        return purchasePrice;
      },

      /**
       * Sale format attached to the catalog entry, else the product sale format.
       * Never falls back to the product stock format.
       */
      getCatalogEntrySaleFormatData(catalogentry) {
        if (!catalogentry) {
          return null;
        }
        if (catalogentry.format && catalogentry.format_data) {
          return catalogentry.format_data;
        }
        const product = catalogentry.product_data;
        if (product?.sale_format_data) {
          return product.sale_format_data;
        }
        // Provider catalog lists omit product.sale_format_data; format_data
        // on the entry is sale-format-only (never stock format).
        if (catalogentry.format_data && !product?.format_data) {
          return catalogentry.format_data;
        }
        return null;
      },

      formatCatalogEntrySaleContent(catalogentry) {
        const formatData = this.getCatalogEntrySaleFormatData(catalogentry);
        if (formatData) {
          return (
            this.getFormatDisplayLabel(formatData, 1)
            || formatData.name
            || null
          );
        }
        if (
          catalogentry?.sale_content_quantity == null
          || catalogentry?.sale_content_quantity === ""
          || !catalogentry?.sale_content_unit
        ) {
          return null;
        }
        try {
          const normalized = new Decimal(String(catalogentry.sale_content_quantity))
            .toDecimalPlaces(3)
            .toString();
          return `${normalized} ${catalogentry.sale_content_unit}`;
        } catch {
          return `${catalogentry.sale_content_quantity} ${catalogentry.sale_content_unit}`;
        }
      },

      /** Parse `ce:<catalogentryId>` unit tokens from quantity inputs. */
      parseCatalogEntryUnitValue(unit) {
        if (typeof unit !== 'string' || !unit.startsWith('ce:')) {
          return null;
        }
        const id = Number.parseInt(unit.slice(3), 10);
        return Number.isNaN(id) ? null : id;
      },

      catalogEntryUnitValue(catalogentryId) {
        return `ce:${catalogentryId}`;
      },

      /**
       * Sale unit label for a catalog entry (format name, custom label, etc.).
       * Mirrors backend get_catalogentry_sale_unit_label.
       */
      getCatalogEntrySaleUnitLabel(catalogentry, quantity = 1) {
        if (!catalogentry) {
          return null;
        }
        const formatData = this.getCatalogEntrySaleFormatData(catalogentry);
        if (formatData) {
          const label = this.getFormatDisplayLabel(formatData, quantity);
          if (label) {
            return label;
          }
        }
        if (
          !catalogentry.format
          && catalogentry.sale_content_quantity != null
          && catalogentry.sale_content_unit
        ) {
          return (
            catalogentry.label
            || this.formatCatalogEntrySaleContent(catalogentry)
          );
        }
        return null;
      },

      /**
       * Unit selector options when a product has multiple catalog entries
       * (default + sale formats) in the same catalog.
       */
      buildBillingCatalogEntryUnitOptions(catalogEntries, catalogId) {
        if (!catalogId) {
          return [];
        }
        const entries = (catalogEntries || []).filter((entry) => {
          if (entry.archived || entry.catalog !== catalogId) {
            return false;
          }
          // Ad hoc sale content without format — not a selectable variant.
          if (!entry.format && entry.sale_content_quantity != null) {
            return false;
          }
          return true;
        });
        if (entries.length === 0) {
          return [];
        }
        return entries.map((entry) => ({
          text:
            this.getCatalogEntrySaleUnitLabel(entry)
            || this.formatCatalogEntrySaleContent(entry)
            || entry.label
            || String(entry.id),
          value: this.catalogEntryUnitValue(entry.id),
        }));
      },

      convertEntryContentToStock(product, content, entryQty) {
        if (!product || !content) {
          return null;
        }
        const qty = new Decimal(entryQty);
        const totalContent = qty.mul(content.qty);
        const stockUnit = product.stock_unit || 'pc';
        const quantizePositive = (value) => {
          // Match API ROUND_DOWN to 3dp, but keep a minimal step when sale qty > 0
          // so small portions vs large palettes do not collapse to 0.
          let quantized = value.toDecimalPlaces(3, Decimal.ROUND_DOWN);
          if (quantized.eq(0) && qty.gt(0)) {
            quantized = new Decimal('0.001');
          }
          return quantized.toNumber();
        };
        if (content.unit === stockUnit) {
          return quantizePositive(totalContent);
        }
        const contentDimension = this.getUnitDimension(content.unit);
        const stockDimension = this.getUnitDimension(stockUnit);
        if (contentDimension === stockDimension) {
          const converted = this.convertUnitQuantity(
            totalContent,
            content.unit,
            stockUnit,
          );
          return converted ? quantizePositive(converted) : null;
        }
        if (stockUnit === 'pc' && ['weight', 'volume'].includes(contentDimension)) {
          const pieceContent = this.getPieceContent(product);
          if (!pieceContent) {
            return null;
          }
          const qtyInPieceUnit = this.convertUnitQuantity(
            totalContent,
            content.unit,
            pieceContent.unit,
          );
          return qtyInPieceUnit
            ? quantizePositive(qtyInPieceUnit.div(pieceContent.qty))
            : null;
        }
        return null;
      },

      convertEntrySaleToStockQuantity(catalogentry, entryQty) {
        if (entryQty == null || entryQty === '') {
          return null;
        }
        const content = this.getCatalogEntrySaleContent(catalogentry);
        if (!content) {
          return null;
        }
        const product = catalogentry.product_data;
        return this.convertEntryContentToStock(product, content, entryQty);
      },

      convertEntryStockToSaleQuantity(catalogentry, stockQty) {
        if (stockQty == null || stockQty === '') {
          return null;
        }
        const content = this.getCatalogEntrySaleContent(catalogentry);
        const product = catalogentry?.product_data;
        if (!content || !product) {
          return null;
        }
        const stockUnit = product.stock_unit || 'pc';
        const quantity = new Decimal(stockQty);
        if (content.unit === stockUnit) {
          return quantity.div(content.qty).toDecimalPlaces(3).toNumber();
        }
        const contentDimension = this.getUnitDimension(content.unit);
        const stockDimension = this.getUnitDimension(stockUnit);
        if (contentDimension === stockDimension) {
          const qtyInContent = this.convertUnitQuantity(
            quantity,
            stockUnit,
            content.unit,
          );
          return qtyInContent
            ? qtyInContent.div(content.qty).toDecimalPlaces(3).toNumber()
            : null;
        }
        if (stockUnit === 'pc' && ['weight', 'volume'].includes(contentDimension)) {
          const pieceContent = this.getPieceContent(product);
          if (!pieceContent) {
            return null;
          }
          const qtyInContent = this.convertUnitQuantity(
            quantity.mul(pieceContent.qty),
            pieceContent.unit,
            content.unit,
          );
          return qtyInContent
            ? qtyInContent.div(content.qty).toDecimalPlaces(3).toNumber()
            : null;
        }
        return null;
      },

      getCatalogEntrySaleQuantityInputConfig(catalogentry) {
        if (this.catalogEntryHasSaleContent(catalogentry)) {
          return {
            unitType: 'piece',
            allowedUnits: ['pc'],
            allowDecimalQuantity: false,
          };
        }
        const product = catalogentry?.product_data;
        return this.getSaleQuantityInputConfig(product);
      },

      /**
       * Sale quantity of a line, falling back to count.
       * Legacy rows persisted the implicit backend default: a zero sale
       * quantity alongside a non-zero count means "no sale quantity".
       */
      getSaleQuantityOrCount(item) {
        if (!item) {
          return null;
        }
        const zeroArtifact =
          item.sale_quantity != null
          && Number(item.sale_quantity) === 0
          && Number(item.count || 0) !== 0;
        if (zeroArtifact) {
          return item.count;
        }
        return item.sale_quantity ?? item.count;
      },

      /**
       * Contenance for a transactional line: snapshot first, then catalog format.
       */
      getLineSaleContentFields(item) {
        const snapshotUnit = (item?.sale_content_unit || '').trim();
        if (
          item?.sale_content_quantity != null
          && item.sale_content_quantity !== ''
          && snapshotUnit
          && snapshotUnit !== 'pc'
        ) {
          return {
            content_quantity: item.sale_content_quantity,
            content_unit: snapshotUnit,
          };
        }
        // Logistics movement/line snapshots use content_* (not sale_content_*).
        const logisticsUnit = (item?.content_unit || '').trim();
        if (
          item?.content_quantity != null
          && item.content_quantity !== ''
          && logisticsUnit
          && logisticsUnit !== 'pc'
        ) {
          return {
            content_quantity: item.content_quantity,
            content_unit: logisticsUnit,
          };
        }
        const formatData =
          item?.catalogentry_data?.format_data
          || item?.catalogentry_data?.product_data?.sale_format_data
          || item?.catalogentry_data?.product_data?.format_data;
        const formatUnit = (formatData?.content_unit || '').trim();
        if (
          formatData?.content_quantity != null
          && formatUnit
          && formatUnit !== 'pc'
        ) {
          return {
            content_quantity: formatData.content_quantity,
            content_unit: formatUnit,
          };
        }
        const entry = item?.catalogentry_data;
        const entryUnit = (entry?.sale_content_unit || '').trim();
        if (
          entry?.sale_content_quantity != null
          && entryUnit
          && entryUnit !== 'pc'
        ) {
          return {
            content_quantity: entry.sale_content_quantity,
            content_unit: entryUnit,
          };
        }
        const display = entry?.sale_content_display;
        const displayUnit = (display?.unit || '').trim();
        if (
          display?.quantity != null
          && display.quantity !== ''
          && displayUnit
          && displayUnit !== 'pc'
        ) {
          return {
            content_quantity: display.quantity,
            content_unit: displayUnit,
          };
        }
        const product = entry?.product_data;
        const productUnit = (product?.sale_unit_content_unit || '').trim();
        if (
          product?.sale_unit_content_quantity != null
          && product.sale_unit_content_quantity !== ''
          && productUnit
          && productUnit !== 'pc'
        ) {
          return {
            content_quantity: product.sale_unit_content_quantity,
            content_unit: productUnit,
          };
        }
        return null;
      },

      /**
       * Append contenance to a container sale-format label (e.g. "Cagette" →
       * "Cagette 12 kg"). Mirrors API get_line_sale_format_display_label.
       */
      appendContentToSaleFormatLabel(containerLabel, item) {
        const label = (containerLabel || '').trim();
        if (!label || label === 'pc') {
          return '';
        }
        const bareUnits = new Set(['pc', 'kg', 'g', 'L', 'cl', 'ml']);
        if (bareUnits.has(label)) {
          return '';
        }
        const contentFields = this.getLineSaleContentFields(item);
        const content = contentFields
          ? this.formatContentCaption(contentFields)
          : null;
        if (!content) {
          return this.capitalizeFormatLabel(label);
        }
        const stripped =
          this.stripCapacityFromFormatLabel(label, contentFields) || label;
        return this.capitalizeFormatLabel(`${stripped} ${content}`);
      },

      /**
       * Custom sale format label for a line (singular, e.g. "Cagette 17.5 kg"),
       * or empty when only a bare unit (pc/kg/L…) applies.
       */
      getLineSaleFormatLabel(item) {
        if (!item) {
          return '';
        }
        const saleFormatData = this.getOrderItemSaleFormatData(item);
        if (saleFormatData) {
          return this.getFormatDisplayLabel(saleFormatData, 1) || '';
        }
        const singular = (item?.sale_unit_label || '').trim();
        const hasSnapshotLabel = Boolean(singular && singular !== 'pc');
        const label = hasSnapshotLabel
          ? singular
          : (this.getOrderItemSaleFormatLabel(item) || '').trim();
        if (!label || label === 'pc') {
          return '';
        }
        if (!hasSnapshotLabel) {
          // getOrderItemSaleFormatLabel already includes contenance when possible.
          const bareUnits = new Set(['pc', 'kg', 'g', 'L', 'cl', 'ml']);
          if (bareUnits.has(label)) {
            return '';
          }
          return this.capitalizeFormatLabel(label);
        }
        return this.appendContentToSaleFormatLabel(label, item);
      },

      /**
       * Quantity for invoice tables: number only when a sale format exists;
       * otherwise number + bare unit (e.g. "1,5 kg").
       */
      formatSaleQuantityNumber(item) {
        if (!item) {
          return '';
        }
        const saleQty = this.getSaleQuantityOrCount(item);
        if (saleQty == null) {
          return item.count != null ? formatQuantity(item.count) : '';
        }
        const qtyText = formatQuantity(saleQty);
        if (this.getLineSaleFormatLabel(item)) {
          return qtyText;
        }
        const saleUnit = item.sale_unit || item.stock_unit;
        if (saleUnit && saleUnit !== 'pc') {
          return this.formatQuantityWithLabel(qtyText, saleUnit);
        }
        return qtyText;
      },

      formatSaleQuantityDisplay(item) {
        if (!item) {
          return '';
        }
        const saleQty = this.getSaleQuantityOrCount(item);
        const saleUnit = item.sale_unit || item.stock_unit;
        if (saleQty == null) {
          return item.count != null ? formatQuantity(item.count) : '';
        }
        const saleFormatData = this.getOrderItemSaleFormatData(item);
        if (saleFormatData) {
          return this.getFormatQuantityLabel(saleFormatData, saleQty);
        }
        // Prefer snapshotted line labels / content over live catalog/product.
        const hasSnapshotLabel = Boolean((item?.sale_unit_label || '').trim());
        let label = hasSnapshotLabel
          ? this.resolveSnapshottedSaleUnitLabel(item, saleQty)
          : this.getOrderItemSaleFormatLabel(item);
        if (hasSnapshotLabel && label) {
          label = this.appendContentToSaleFormatLabel(label, item) || label;
        }
        label = label || saleUnit;
        const saleQtyText = formatQuantity(saleQty);
        // The piece unit is implied: only display it when a custom label was set.
        const displayLabel = label && label !== 'pc' ? label : '';
        return displayLabel
          ? this.formatQuantityWithLabel(saleQtyText, displayLabel)
          : saleQtyText;
      },

      formatStockQuantityAsSaleDisplay(item, stockQuantity) {
        if (!item || stockQuantity == null || stockQuantity === '') {
          return '';
        }
        const stockQty = Number(stockQuantity);
        if (Number.isNaN(stockQty)) {
          return '';
        }
        const saleQtyForLine = this.getSaleQuantityOrCount(item);
        if (
          item.sale_unit
          && saleQtyForLine != null
          && item.count != null
          && Number(item.count) !== 0
        ) {
          const saleQty = (Number(saleQtyForLine) / Number(item.count)) * stockQty;
          return this.formatSaleQuantityDisplay({
            ...item,
            sale_quantity: saleQty,
            count: stockQty,
          });
        }
        return this.formatSaleQuantityDisplay({
          ...item,
          sale_quantity: stockQty,
          count: stockQty,
        });
      },

      /**
       * Remaining "to schedule" quantity in sale units.
       * Stock ``to_schedule`` can collapse to 0 when ``count`` was quantized away
       * (e.g. small portions vs large palette) while ``sale_quantity`` stays positive.
       * Prefer the sale remaining when stock fields are missing/zeroed.
       */
      getOrderItemToScheduleSaleQuantity(item) {
        if (!item) {
          return 0;
        }
        const saleOrdered = Number(this.getSaleQuantityOrCount(item));
        const stockCount = Number(item.count);
        const toScheduleStock = Number(item.to_schedule);
        const deliveredStock = Number(item.delivered) || 0;
        const scheduledStock = Number(item.scheduled) || 0;

        const roundSale = (value) => {
          try {
            return new Decimal(String(value))
              .toDecimalPlaces(3, Decimal.ROUND_HALF_UP)
              .toNumber();
          } catch {
            return Number(value) || 0;
          }
        };

        // Stock count lost (quantized to 0): remaining work is the unordered sale qty.
        if (
          saleOrdered > 0
          && (!Number.isFinite(stockCount) || stockCount <= 0)
        ) {
          return saleOrdered;
        }

        if (
          saleOrdered > 0
          && Number.isFinite(stockCount)
          && stockCount > 0
          && Number.isFinite(toScheduleStock)
        ) {
          const toScheduleSale = roundSale(
            (saleOrdered / stockCount) * toScheduleStock,
          );
          if (toScheduleSale > 0) {
            return toScheduleSale;
          }
          const deliveredSale = roundSale(
            (saleOrdered / stockCount) * deliveredStock,
          );
          const remainingSale = Math.max(saleOrdered - deliveredSale, 0);
          if (
            remainingSale > 0
            && toScheduleStock === 0
            && scheduledStock <= deliveredStock
          ) {
            return remainingSale;
          }
          return 0;
        }

        if (saleOrdered > 0) {
          return saleOrdered;
        }

        return Number.isFinite(toScheduleStock) ? toScheduleStock : 0;
      },

      formatOrderItemToScheduleAsSaleNumber(item) {
        return formatQuantity(this.getOrderItemToScheduleSaleQuantity(item));
      },

      orderItemHasSaleQuantityToSchedule(item) {
        return this.getOrderItemToScheduleSaleQuantity(item) > 0;
      },

      /**
       * Like formatStockQuantityAsSaleDisplay but number-only when a sale
       * format label is shown elsewhere (e.g. reference column).
       */
      formatStockQuantityAsSaleNumber(item, stockQuantity) {
        if (!item || stockQuantity == null || stockQuantity === '') {
          return '';
        }
        const stockQty = Number(stockQuantity);
        if (Number.isNaN(stockQty)) {
          return '';
        }
        const saleQtyForLine = this.getSaleQuantityOrCount(item);
        if (
          item.sale_unit
          && saleQtyForLine != null
          && item.count != null
          && Number(item.count) !== 0
        ) {
          const saleQty = (Number(saleQtyForLine) / Number(item.count)) * stockQty;
          return this.formatSaleQuantityNumber({
            ...item,
            sale_quantity: saleQty,
            count: stockQty,
          });
        }
        return this.formatSaleQuantityNumber({
          ...item,
          sale_quantity: stockQty,
          count: stockQty,
        });
      },

      getBillingQuantity(item) {
        if (!item) {
          return null;
        }
        // Mirror backend get_billing_quantity: a non-zero sale quantity
        // overrides count (0 is the legacy "unset" artifact; negative values
        // come from credit lines).
        if (
          item.sale_quantity != null
          && item.sale_quantity !== ''
          && Number(item.sale_quantity) !== 0
        ) {
          return item.sale_quantity;
        }
        return item.count;
      },

      /**
       * Map API product stock_unit to QuantityUnitInput configuration.
       */
      getProductQuantityInputConfig(stockUnit, allowDecimalQuantity = false) {
        const unit = stockUnit || 'pc';
        if (unit === 'pc') {
          return {
            unitType: 'piece',
            allowedUnits: ['pc'],
            allowDecimalQuantity: !!allowDecimalQuantity,
          };
        }
        if (unit === 'kg' || unit === 'g') {
          return {
            unitType: 'weight',
            allowedUnits: unit === 'g' ? ['g'] : ['kg', 'g'],
            allowDecimalQuantity: true,
          };
        }
        if (unit === 'L' || unit === 'cl' || unit === 'ml') {
          let allowedUnits = ['ml'];
          if (unit === 'cl') {
            allowedUnits = ['cl', 'ml'];
          } else if (unit === 'L') {
            allowedUnits = ['L', 'cl', 'ml'];
          }
          return {
            unitType: 'volume',
            allowedUnits,
            allowDecimalQuantity: true,
          };
        }
        return {
          unitType: 'piece',
          allowedUnits: ['pc'],
          allowDecimalQuantity: false,
        };
      },

      /**
       * HTML step attribute for stock quantity inputs.
       */
      getStockQuantityStep(config) {
        if (!config) {
          return '1';
        }
        if (config.unitType === 'piece') {
          // +/- always increments by 1 piece. Decimal quantities remain
          // allowed via typed input when allowDecimalQuantity is true.
          return '1';
        }
        if (config.unitType === 'weight' || config.unitType === 'volume') {
          return '0.001';
        }
        return '1';
      },

      /**
       * Parse user stock quantity input (max 3 decimal places).
       */
      parseStockQuantity(value, { allowDecimal = false, allowNegative = false, allowZero = false } = {}) {
        if (value === '' || value == null) {
          return null;
        }
        let parsed;
        try {
          // Accept comma as decimal separator
          parsed = new Decimal(String(value).trim().replace(",", "."));
        } catch (e) {
          return null;
        }
        if (parsed.isNaN()) {
          return null;
        }
        if (allowNegative) {
          if (parsed.isZero()) {
            return null;
          }
        } else if (allowZero) {
          if (parsed.lt(0)) {
            return null;
          }
        } else if (parsed.lte(0)) {
          return null;
        }
        if (!allowDecimal && !parsed.isInteger()) {
          return null;
        }
        if (parsed.decimalPlaces() > 3) {
          return null;
        }
        return parsed.toDecimalPlaces(3, Decimal.ROUND_DOWN).toString();
      },
      
      /**
       * Get unit label with plural support
       * @param {string} unit - The unit code (g, kg, L, ml, cl, pc)
       * @param {number} [quantity=1] - The quantity to determine singular/plural
       * @returns {string} The complete unit label with quantity
       */
      getUnitLabel(unit, quantity = 1) {
        if (!unit) return '';
        
        const isPlural = Math.abs(quantity) !== 1;
        
        // Map unit codes to their translation keys
        const unitKeyMap = {
          'g': 'gram',
          'kg': 'kilogram', 
          'l': 'liter',
          'ml': 'milliliter',
          'cl': 'centiliter',
          'pc': 'piece'
        };
        
        const baseUnitKey = unitKeyMap[unit];
        if (!baseUnitKey) return '';
        
        // Get the appropriate translation key
        const translationKey = isPlural ? `units.${baseUnitKey}s` : `units.${baseUnitKey}`;
        
        return Vue.prototype.$translate(translationKey);
      },
      
      /**
       * Get short unit label for weight and volume units (not for pieces)
       * @param {string} unit - The unit code (g, kg, L, ml, cl, pc)
       * @param {number} [quantity=1] - The quantity to determine singular/plural
       * @returns {string} The short unit label or empty string for pieces
       */
      getShortUnitLabel(unitType, quantity = 1) {
        if (!unitType) return '';
        let unit = '';
        if (unitType === 'weight') {
            const kg = new Decimal(quantity).div(1000);
            if (kg.gte(1)) {
                unit = 'kg';
            } else {
                unit = 'g';
            }
        } else if (unitType === 'volume') {
            const L = new Decimal(quantity).div(1000);
            if (L.gte(1)) {
                unit = 'l';
            } else if (L.gte(0.1)) {
                unit = 'cl';
            } else {
                unit = 'ml';
            }
        } else if (unitType === 'unit') {
            if (quantity === 1) {
                unit = 'pc';
            } else {
                unit = 'pcs';
            }
        }
        return unit ? Vue.prototype.$translate(`unitSymbols.${unit}`) : '';
      },
      
      /**
       * Get formatted quantity with short unit label
       * @param {number} quantity - The quantity value
       * @param {string} unit - The unit code (g, kg, L, ml, cl, pc)
       * @param {boolean} [disableConversion=false] - Disable automatic unit conversion
       * @returns {string} Formatted string with quantity and short unit
       */
      formatQuantityWithShortUnit(quantity, unitType, disableConversion = false) {
        if (!unitType || unitType === 'piece') {
          const qty = new Decimal(quantity || 0);
          // Use formatNumberLocale for piece quantities too
          return this.formatNumberLocale(qty.toNumber());
        }
        if (!quantity && quantity !== 0) return '0';
        
        let finalQuantity = new Decimal(quantity || 0);
        let finalUnit = '';
        
        // Stock is now stored in kg/L, convert to g/ml for display when < 1 for readability
        if (!disableConversion) {
          if (unitType === 'weight') {
            // Stock is in kg, convert to g if < 1kg for readability
            if (finalQuantity.abs().lt(1) && finalQuantity.abs().gt(0)) {
                finalQuantity = finalQuantity.mul(1000);
                finalUnit = 'g';
            } else {
                finalUnit = 'kg';
            }
          } else if (unitType === 'volume') {
            // Stock is in L, convert to ml/cl if < 1L for readability
            if (finalQuantity.abs().lt(0.01)) {
                finalQuantity = finalQuantity.mul(1000);
                finalUnit = 'ml';
            } else if (finalQuantity.abs().lt(1)) {
                finalQuantity = finalQuantity.mul(100);
                finalUnit = 'cl';
            } else {
                finalUnit = 'L';
            }
          }
        } else {
          // No conversion, use the appropriate unit based on unitType
          if (unitType === 'weight') {
            finalUnit = 'kg';
          } else if (unitType === 'volume') {
            finalUnit = 'L';
          }
        }
        
        const unitLabel = Vue.prototype.$translate(`unitSymbols.${finalUnit}`);
        // Normalize first to remove trailing zeros, then format with locale
        const normalizedValue = parseFloat(this.normalizeDecimalString(finalQuantity.toString()));
        const formattedQuantity = this.formatNumberLocale(normalizedValue);
        return `${formattedQuantity} ${unitLabel}`;
      },
      
      /**
       * Replace comma decimal separator with a dot, keeping the raw string
       * otherwise (useful to normalize while the user is still typing).
       * @param {string|number|null|undefined} value
       * @returns {string|number|null|undefined}
       */
      normalizeDecimalString(value) {
        if (value === null || value === undefined || value === "") {
          return value;
        }
        return String(value).replace(",", ".");
      },

      /**
       * Normalize user-entered decimal (e.g. "1,7") for API numeric fields.
       * @param {string|number|null|undefined} value
       * @returns {string|null} Dot-separated decimal string, or null if empty/invalid
       */
      normalizeAmountForApi(value) {
        if (value === null || value === undefined || value === "") {
          return null;
        }
        const normalized = String(value).trim().replace(/\s/g, "").replace(",", ".");
        const parsed = Number.parseFloat(normalized);
        if (Number.isNaN(parsed)) {
          return null;
        }
        return String(parsed);
      },
      
      /**
       * Format quantity for input display (convert kg/L to g/ml if < 1 for easier input)
       * @param {number|Decimal} quantity - The quantity value (in kg/L)
       * @param {string} unitType - The unit type (weight, volume, unit)
       * @returns {object} { value: number, unit: string } for input component
       */
      formatQuantityForInput(quantity, unitType) {
        if (!unitType || unitType === 'piece') {
          return { value: new Decimal(quantity || 0).toNumber(), unit: 'pc' };
        }
        
        const qty = new Decimal(quantity || 0);
        
        if (unitType === 'weight') {
          if (qty.abs().lt(1) && qty.abs().gt(0)) {
            return { value: qty.mul(1000).toNumber(), unit: 'g' };
          }
          return { value: qty.toNumber(), unit: 'kg' };
        } else if (unitType === 'volume') {
          if (qty.abs().lt(0.01)) {
            return { value: qty.mul(1000).toNumber(), unit: 'ml' };
          } else if (qty.abs().lt(1)) {
            return { value: qty.mul(100).toNumber(), unit: 'cl' };
          }
          return { value: qty.toNumber(), unit: 'L' };
        }
        
        return { value: qty.toNumber(), unit: 'pc' };
      },
      
      /**
       * Format quantity for display with intelligent conversion
       * @param {number|Decimal} quantity - The quantity value (in kg/L for weight/volume)
       * @param {string} unitType - The unit type (weight, volume, unit)
       * @returns {string} Formatted quantity string
       */
      formatQuantityForDisplay(quantity, unitType) {
        return this.formatQuantityWithShortUnit(quantity, unitType, false);
      },

      getUnitTypeLabel(unitType) {
        if (!unitType) return '';
        if (unitType === 'weight') {
            return Vue.prototype.$translate("unitSymbols.kg");
        } else if (unitType === 'volume') {
            return Vue.prototype.$translate("unitSymbols.l");
        }
        return '';
      },

      formatQuantityWithUnitTypeLabel(quantity, unitType) {
        if (quantity === null || quantity === undefined) return '';
        if (quantity === 0) return '0';
        // Use formatQuantityWithShortUnit for better formatting with conversions
        return this.formatQuantityWithShortUnit(quantity, unitType, false);
      },

      formatContentCaption(format) {
        if (!format?.content_quantity || !format?.content_unit) {
          return null;
        }
        let quantity = format.content_quantity;
        let unit = format.content_unit;
        try {
          const qty = new Decimal(String(quantity));
          if (unit === 'cl' && qty.gte(100) && qty.mod(100).eq(0)) {
            quantity = qty.div(100).toString();
            unit = 'L';
          } else if (unit === 'g' && qty.gte(1000) && qty.mod(1000).eq(0)) {
            quantity = qty.div(1000).toString();
            unit = 'kg';
          }
        } catch {
          // keep raw values
        }
        const quantityText = this.formatDecimalInputValue(quantity, 3);
        return `${quantityText} ${unit}`;
      },

      /**
       * Composed user-facing label: "{container} {content}" (e.g. "Verre 12 cl").
       */
      getFormatDisplayLabel(formatData, quantity = 1, language = null) {
        if (!formatData) {
          return '';
        }
        // Prefer quantity-aware singular/plural labels when available.
        const container =
          this.getFormatLabel(formatData, quantity, language)
          || formatData.container_label
          || this.getFormatGroupLabel(formatData);
        const content = this.formatContentCaption(formatData);
        if (container && content) {
          const containerLower = String(container).toLowerCase();
          const contentLower = String(content).toLowerCase();
          const alreadyHasContent =
            containerLower.endsWith(contentLower)
            || this.contentDisplayVariants(
              formatData.content_quantity,
              formatData.content_unit,
            ).some((variant) =>
              containerLower.endsWith(String(variant).toLowerCase()),
            );
          if (alreadyHasContent) {
            const stripped =
              this.stripCapacityFromFormatLabel(container, formatData)
              || container;
            return this.capitalizeFormatLabel(`${stripped} ${content}`);
          }
          return this.capitalizeFormatLabel(`${container} ${content}`);
        }
        if (formatData.name && !content) {
          return this.capitalizeFormatLabel(formatData.name);
        }
        return this.capitalizeFormatLabel(container || content || '');
      },

      formatOptionLabel(format) {
        return this.getFormatDisplayLabel(format, 1);
      },

      formatGroupHeader(format) {
        const label = this.getFormatGroupLabel(format);
        if (!label) {
          return '';
        }
        return label.charAt(0).toUpperCase() + label.slice(1);
      },

      buildFormatSelectItems({
        formats = [],
        variant = 'plain',
        unitOptionLabel = null,
        showUnitOption = false,
        showCustomOption = false,
        showCreateOption = false,
        customOptionLabel = null,
        createOptionLabel = null,
        defaultEntryLabel = null,
        formatFilter = null,
      } = {}) {
        const actionItems = [];
        if (showCustomOption) {
          actionItems.push({
            text: customOptionLabel || 'Customize',
            value: FORMAT_SELECT_CUSTOM,
          });
        }
        if (showCreateOption) {
          actionItems.push({
            text: createOptionLabel || 'Create a format',
            value: FORMAT_SELECT_CREATE,
          });
        }
        if (showUnitOption) {
          actionItems.push({
            text: unitOptionLabel || defaultEntryLabel || 'Per unit',
            value: FORMAT_SELECT_UNIT,
          });
        }

        if (variant === 'plain') {
          const filtered = formatFilter
            ? formats.filter((format) => formatFilter(format))
            : formats;
          return this.sortFormatsByCapacity(filtered);
        }

        if (variant === 'code') {
          const items = [
            {
              text: defaultEntryLabel || '—',
              value: '',
            },
          ];
          let lastGroupKey = null;
          this.sortFormatsByCapacity(formats).forEach((format) => {
            if (formatFilter && !formatFilter(format)) {
              return;
            }
            const groupKey = this.getFormatGroupKey(format);
            if (groupKey !== lastGroupKey) {
              items.push({ header: this.formatGroupHeader(format) });
              lastGroupKey = groupKey;
            }
            items.push({
              text: this.formatOptionLabel(format),
              value: format.code,
            });
          });
          return items;
        }

        const formatItems = [];
        let lastGroupKey = null;
        // Always order by group then capacity (sale/grouped); headers only for grouped.
        this.sortFormatsByCapacity(formats).forEach((format) => {
          if (formatFilter && !formatFilter(format)) {
            return;
          }
          if (variant === 'grouped') {
            const groupKey = this.getFormatGroupKey(format);
            if (groupKey !== lastGroupKey) {
              formatItems.push({ header: this.formatGroupHeader(format) });
              lastGroupKey = groupKey;
            }
          }
          formatItems.push({
            text: this.formatOptionLabel(format),
            value: format.id,
          });
        });

        return actionItems.concat(formatItems);
      },

      filterFormatSelectItem(item, queryText, itemText, formats = []) {
        if (item.header) {
          return !queryText;
        }
        if (!queryText) {
          return true;
        }
        if (
          item.value === FORMAT_SELECT_CUSTOM
          || item.value === FORMAT_SELECT_CREATE
          || item.value === FORMAT_SELECT_UNIT
          || item.value === ''
        ) {
          return cascadeFormatLabelMatchesSearch(itemText, queryText);
        }
        if (cascadeFormatLabelMatchesSearch(itemText, queryText)) {
          return true;
        }
        const formatId = item.value ?? item.id;
        const format = formats.find(
          (entry) => entry.id === formatId || entry.code === formatId,
        );
        if (!format) {
          return false;
        }
        if (cascadeFormatLabelMatchesSearch(this.getFormatContainerLabel(format), queryText)) {
          return true;
        }
        return this.formatMatchesCapacityQuery(format, queryText);
      },

      buildSdeFormatSelectItems(formats = [], { emptyLabel = "—" } = {}) {
        return this.buildFormatSelectItems({
          formats,
          variant: 'code',
          defaultEntryLabel: emptyLabel,
        });
      },

      resolveSdeFormatSelectLabel(value, formatItems = []) {
        if (value == null || value === "") {
          const emptyItem = formatItems.find(
            (item) => !item.header && (item.value == null || item.value === ""),
          );
          return emptyItem?.text || "";
        }
        const match = formatItems.find(
          (item) => !item.header && item.value === value,
        );
        return match?.text || String(value);
      },
};
